import React, { useState, useEffect } from 'react';
import { safeTrim } from '../utils/validation';
import { Server, Wifi, Activity, Settings, TestTube, Power, Check, X, Plus, Trash2, Edit, Eye, EyeOff, Loader2, CheckCircle, XCircle, RefreshCw, Shield, ShieldOff, Database, Users, AlertTriangle, Webhook, Copy, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { apiClient } from '../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useDualVpn, DualVpnStatusRow, DualVpnScriptModal } from '../components/mikrotik/DualVpnStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MikrotikSettings = () => {
  // 2026-05-28: respect global branch & router selector from header
  const { selectedBranch, selectedRouter, setSelectedRouter } = useApp();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRoleLower = (user?.role || '').toLowerCase();
  const isSuperAdmin = userRoleLower === 'super_admin' || userRoleLower === 'superadmin' || userRoleLower === 'admin';
  const dualVpn = useDualVpn();
  const isOwnerRoleForForm = userRoleLower === 'owner'; // 2026-05-31: hide owner-assign dropdown from owner role
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const r = (u?.role || '').toLowerCase();
      const superA = r === 'super_admin' || r === 'superadmin' || r === 'admin';
      return superA ? 'whitelist' : 'routers';
    } catch (e) { return 'routers'; }
  }); // 'whitelist', 'routers', or 'backup'
  
  // Backup Secrets states
  const [backupSyncing, setBackupSyncing] = useState(false);
  const [backupEnabling, setBackupEnabling] = useState(false);
  const [backupDisabling, setBackupDisabling] = useState(false);
  const [backupSyncResult, setBackupSyncResult] = useState(null);
  
  // Profile Sync states
  const [profileSyncing, setProfileSyncing] = useState(false);
  const [profileSyncResult, setProfileSyncResult] = useState(null);
  const [routers, setRouters] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [branches, setBranches] = useState([]);
  const [owners, setOwners] = useState([]); // 2026-05-31: list of owners for assignment
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRouter, setEditingRouter] = useState(null);
  const [showPassword, setShowPassword] = useState({});
  const [testingRouter, setTestingRouter] = useState(null);
  const [redeployingRouter, setRedeployingRouter] = useState(null);
  const [activeCounts, setActiveCounts] = useState({}); // {router_id: {count, status}}
  const [webhookStatus, setWebhookStatus] = useState({}); // {router_id: {ok, total, status}}
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [routerToDelete, setRouterToDelete] = useState(null);
  // Auto-setup Isolir states
  const [showIsolirSetupModal, setShowIsolirSetupModal] = useState(false);
  const [isolirSetupRouter, setIsolirSetupRouter] = useState(null);
  const [isolirSetupLoading, setIsolirSetupLoading] = useState(false);

  // 2026-05-28: Move Customers Between Routers wizard
  const [showMoveCustModal, setShowMoveCustModal] = useState(false);
  const [moveSourceRouter, setMoveSourceRouter] = useState(null);
  const [moveTargetRouterId, setMoveTargetRouterId] = useState('');
  const [moveCustomers, setMoveCustomers] = useState([]);
  const [moveLoadingList, setMoveLoadingList] = useState(false);
  const [moveSelectedIds, setMoveSelectedIds] = useState(new Set());
  const [moveSearch, setMoveSearch] = useState('');
  const [moveExecuting, setMoveExecuting] = useState(false);
  const [moveResult, setMoveResult] = useState(null);
  
  // Webhook setup states
  const [webhookSetupLoading, setWebhookSetupLoading] = useState(null); 
  const [copySecretsLoading, setCopySecretsLoading] = useState(null); // router.id during preview/execute
  const [copySecretsModal, setCopySecretsModal] = useState({ open: false, router: null, preview: null, executing: false, result: null });
  const [syncRadiusLoading, setSyncRadiusLoading] = useState(null);// router id being setup
  const [syncAllLoading, setSyncAllLoading] = useState(false); // global sync all button
  const [syncRouterProfilesLoading, setSyncRouterProfilesLoading] = useState(null); // router id being synced (per-router profiles)
  // PPPoE webhook API key (event-driven realtime). Injected into copy-paste setup script.
  const [pppoeApiKey, setPppoeApiKey] = useState('')
  
  // VPN credentials for auto-fill
  const [vpnCredentials, setVpnCredentials] = useState([]);
  const [activeVpnConnections, setActiveVpnConnections] = useState([]);
  const [unconfiguredVpn, setUnconfiguredVpn] = useState([]);
  const [selectedVpnUser, setSelectedVpnUser] = useState('');
  
  // Test status states
  const [vpnTestStatus, setVpnTestStatus] = useState(null); // null, 'testing', 'success', 'failed'
  const [apiTestStatus, setApiTestStatus] = useState(null); // null, 'testing', 'success', 'failed'
  const [vpnTestMessage, setVpnTestMessage] = useState('');
  const [apiTestMessage, setApiTestMessage] = useState('');
  
  // Whitelist states
  const [showAddWhitelistModal, setShowAddWhitelistModal] = useState(false);
  const [editingWhitelist, setEditingWhitelist] = useState(null);
  const [whitelistToDelete, setWhitelistToDelete] = useState(null);
  const [showDeleteWhitelistModal, setShowDeleteWhitelistModal] = useState(false);
  const [whitelistFormData, setWhitelistFormData] = useState({
    ip_address: '',
    description: ''
  });
  
  // Search and Pagination states for Whitelist
  const [whitelistSearchQuery, setWhitelistSearchQuery] = useState('');
  const [whitelistCurrentPage, setWhitelistCurrentPage] = useState(1);
  const [whitelistItemsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    router_name: '',
    host: '',
    port: '',
    username: '',
    password: '',
    location: '',
    branch_id: '',
    branch_name: '',
    owner_id: '', // 2026-05-31
    // VPN fields for routers without public IP
    has_public_ip: true,
    vpn_ip: '',
    vpn_interface: 'l2tp-billing',
    auto_create_vpn: false, // Auto create VPN when adding router
    use_vpn_wizard: false, // VPN Wizard Option A: auto-push L2TP via MikroTik API
    temp_public_host: '', // Temporary public IP of MikroTik (used only for initial L2TP push)
    existing_vpn_user_id: '', // VPN Wizard Mode B: use pre-allocated VPN user (skip L2TP push)
    // RADIUS fields
    connection_mode: 'api_only',
    radius_enabled: false,
    radius_nas_identifier: '',
    radius_shared_secret: 'testing123',
    // Additional RADIUS fields
    radius_server_type: 'public', // 'public' or 'vpn'
    radius_server_ip: '103.179.252.149',
    radius_vpn_ip: '10.10.10.1', // VPN IP of RADIUS server
    radius_auth_port: 1812,
    radius_acct_port: 1813,
    radius_timeout: 3000,
    radius_coa_port: 3799,
    // Multi-RADIUS Database config
    radius_host: '',
    radius_port: 3306,
    radius_db_user: '',
    radius_db_password: '',
    radius_db_name: 'radius'
  });
  const [saving, setSaving] = useState(false);
  const [vpnCreatedData, setVpnCreatedData] = useState(null); // Store created VPN data for display
  const [connectionType, setConnectionType] = useState(null); // 'public_ip' or 'vpn_tunnel' - for initial selection
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [scriptRouter, setScriptRouter] = useState(null);

  useEffect(() => {
    fetchRouters();
    fetchActiveCounts();
    fetchWebhookStatus();
    fetchWhitelist();
    fetchBranches();
    fetchOwners();
    fetchVpnCredentials();
    fetchActiveVpn();
    fetchPppoeApiKey();
    // Refresh active counts every 60 seconds
    const _ac = setInterval(fetchActiveCounts, 180000);
    const _ws = setInterval(fetchWebhookStatus, 300000);
    return () => clearInterval(_ac);
  }, []);

  // ============ BACKUP SECRETS FUNCTIONS ============
  
  // Sync all service plans/profiles to MikroTik
  const handleSyncProfiles = async () => {
    setProfileSyncing(true);
    setProfileSyncResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/sync-all-profiles`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        setProfileSyncResult(response.data);
      } else {
        toast.error(`❌ ${response.data.message || 'Sync gagal'}`);
      }
    } catch (error) {
      toast.error(`❌ Error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProfileSyncing(false);
    }
  };
  
  // Sync all customers to MikroTik as backup secrets (disabled)
  const handleSyncBackupSecrets = async () => {
    setBackupSyncing(true);
    setBackupSyncResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/sync-backup-secrets`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        setBackupSyncResult(response.data);
      } else {
        toast.error(`❌ ${response.data.message || 'Sync gagal'}`);
      }
    } catch (error) {
      toast.error(`❌ Error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setBackupSyncing(false);
    }
  };
  
  // Enable all backup secrets (RADIUS offline mode)
  const handleEnableBackupSecrets = async () => {
    if (!window.confirm('⚠️ PERHATIAN!\n\nAnda akan MENGAKTIFKAN semua backup secrets.\nGunakan ini hanya jika RADIUS server OFFLINE.\n\nLanjutkan?')) {
      return;
    }
    
    setBackupEnabling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/enable-backup-secrets`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
      } else {
        toast.error(`❌ ${response.data.message || 'Enable gagal'}`);
      }
    } catch (error) {
      toast.error(`❌ Error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setBackupEnabling(false);
    }
  };
  
  // Disable all backup secrets (RADIUS back online)
  const handleDisableBackupSecrets = async () => {
    if (!window.confirm('Anda akan MENONAKTIFKAN semua backup secrets.\nGunakan ini setelah RADIUS server kembali ONLINE.\n\nLanjutkan?')) {
      return;
    }
    
    setBackupDisabling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/disable-backup-secrets`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
      } else {
        toast.error(`❌ ${response.data.message || 'Disable gagal'}`);
      }
    } catch (error) {
      toast.error(`❌ Error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setBackupDisabling(false);
    }
  };

  // Fetch VPN credentials for auto-fill
  const fetchVpnCredentials = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/tenant-vpn/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Handle both formats: { data: { users: [...] } } or { data: [...] }
        const users = response.data.data?.users || response.data.data || [];
        if (Array.isArray(users)) {
          setVpnCredentials(users);
        } else {
          setVpnCredentials([]);
        }
      } else {
        setVpnCredentials([]);
      }
    } catch (error) {
      console.warn('Failed to fetch VPN credentials:', error.message);
      setVpnCredentials([]);
    }
  };

  // Fetch active VPN connections from server (auto-detect)
  const fetchActiveVpn = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/v2/mikrotiks/vpn-active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setActiveVpnConnections(response.data.data.active_vpn || []);
        setUnconfiguredVpn(response.data.data.unconfigured || []);
      }
    } catch (error) {
      console.warn("Failed to fetch active VPN:", error.message);
    }
  };

  // Handle VPN user selection - auto-fill IP
  const handleVpnUserSelect = async (vpnUsername) => {
    setSelectedVpnUser(vpnUsername);
    
    // Reset test status when VPN user changes
    setVpnTestStatus(null);
    setApiTestStatus(null);
    
    if (!vpnUsername) {
      return; // No user selected, don't change anything
    }
    
    // Try to get credentials from API to get local_ip
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/tenant-vpn/credentials/${vpnUsername}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.data) {
        const cred = response.data.data;
        let localIp = null;
        
        // Check if credentials have local_ip stored
        if (cred.local_ip && /^[0-9.]+$/.test(cred.local_ip)) {
          localIp = cred.local_ip;
        } else if (cred.mikrotik_script) {
          // Parse local IP from mikrotik_script
          const localIpMatch = cred.mikrotik_script.match(/local-address=([0-9.]+)/);
          if (localIpMatch && localIpMatch[1]) {
            localIp = localIpMatch[1];
          }
        }
        
        if (localIp) {
          setFormData(prev => ({
            ...prev,
            host: localIp,
            has_public_ip: false
          }));
          toast.success(`IP VPN ${localIp} otomatis diisi dari kredensial ${vpnUsername}`);
        } else {
          // No IP found - DON'T change the host field, just show warning
          toast.warning(`IP VPN tidak ditemukan untuk ${vpnUsername}. Silakan isi IP VPN manual.`);
        }
      } else {
        toast.warning(`Kredensial ${vpnUsername} tidak ditemukan. Silakan isi IP VPN manual.`);
      }
    } catch (error) {
      console.warn('Could not fetch VPN credentials:', error.message);
      toast.warning(`Gagal mengambil kredensial ${vpnUsername}. Silakan isi IP VPN manual.`);
    }
  };

  // Test VPN Connection
  const testVpnConnection = async () => {
    if (!formData.host) {
      toast.error('Masukkan IP VPN router terlebih dahulu');
      return;
    }

    setVpnTestStatus('testing');
    setVpnTestMessage('Mengetes koneksi VPN...');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/test-vpn`, {
        host: formData.host,
        port: formData.port
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });

      if (response.data.success) {
        setVpnTestStatus('success');
        setVpnTestMessage(response.data.message || 'Koneksi VPN berhasil!');
        toast.success('Koneksi VPN berhasil!');
      } else {
        setVpnTestStatus('failed');
        setVpnTestMessage(response.data.message || 'Koneksi VPN gagal');
        toast.error(response.data.message || 'Koneksi VPN gagal');
      }
    } catch (error) {
      setVpnTestStatus('failed');
      const errorMsg = error.response?.data?.message || error.message || 'Koneksi VPN gagal';
      setVpnTestMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  // Test MikroTik API Connection
  const [detectingPort, setDetectingPort] = useState(false);

  const detectApiPort = async () => {
    if (!formData.host) {
      toast.error('Masukkan IP/Host MikroTik terlebih dahulu');
      return;
    }
    try {
      setDetectingPort(true);
      const response = await axios.post(
        `${API_URL}/api/mikrotik/detect-api-port`,
        { host: formData.host },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const data = response.data || {};
      // Build a friendly summary of what was found
      const probes = data.probed || [];
      const found = probes.filter((p) => p.reachable).map((p) => {
        if (p.service === 'api') return `API ${p.port} (${p.type.toUpperCase()})`;
        if (p.service === 'winbox') return 'WinBox 8291';
        if (p.service === 'ssh') return 'SSH 22';
        return `${p.service}:${p.port}`;
      });
      if (data.success) {
        const p = data.port;
        setFormData((prev) => ({ ...prev, port: p }));
        const summary = found.length ? ` (Terdeteksi: ${found.join(', ')})` : '';
        toast.success(`✅ Port ${p} dipilih.${summary}`);
      } else if (data.likely_mikrotik) {
        // looks like MikroTik but API closed
        toast.warning(data.message || 'API tertutup tapi host kemungkinan MikroTik.', { duration: 8000 });
      } else {
        toast.error(data.message || 'Tidak ada port MikroTik yang terbuka', { duration: 7000 });
      }
    } catch (err) {
      toast.error(`Auto-detect gagal: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDetectingPort(false);
    }
  };

  const testApiConnection = async () => {
    if (!formData.host || !formData.username) {
      toast.error('Masukkan IP dan Username MikroTik terlebih dahulu');
      return;
    }

    // For new router (not editing), password is required
    if (!editingRouter && !formData.password) {
      toast.error('Masukkan Password MikroTik');
      return;
    }

    setApiTestStatus('testing');
    setApiTestMessage('Mengetes koneksi API MikroTik...');

    // Build request data - always send password if provided
    const requestData = {
      host: formData.host,
      port: formData.port || 8728,
      username: formData.username,
      owner_id: formData.owner_id === '' ? null : formData.owner_id
    };
    
    // Add password if provided
    if (safeTrim(formData.password) !== '') {
      requestData.password = formData.password;
    }
    
    // Add router_id for editing (to get password from DB if not provided)
    if (editingRouter && editingRouter.id) {
      requestData.router_id = editingRouter.id;
    }

    console.log('[TestAPI] Request data:', { ...requestData, password: requestData.password ? '***' : undefined });

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/test-connection-direct`, requestData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      if (response.data.success) {
        setApiTestStatus('success');
        const data = response.data.data || {};
        setApiTestMessage(`Berhasil! ${data.platform || ''} ${data.version || ''}`);
        toast.success('Koneksi API MikroTik berhasil!');
      } else {
        setApiTestStatus('failed');
        setApiTestMessage(response.data.message || 'Koneksi API gagal');
        toast.error(response.data.message || 'Koneksi API gagal');
      }
    } catch (error) {
      setApiTestStatus('failed');
      const errorMsg = error.response?.data?.message || error.message || 'Koneksi API gagal';
      setApiTestMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  // Check if can save (based on connection mode and test status)
  const canSave = () => {
    const mode = formData.connection_mode;
    
    // For RADIUS only mode, no API test needed
    if (mode === 'radius_only') {
      return true;
    }
    
    // For API or Hybrid mode - tests are recommended but not required
    // User can save without testing if they're confident the config is correct
    // (especially useful when testing from preview environment without VPN access)
    return true;
  };

  // Check if tests passed (for UI feedback)
  const testsPassedOrSkipped = () => {
    const mode = formData.connection_mode;
    
    if (mode === 'radius_only') {
      return true;
    }
    
    if (!formData.has_public_ip) {
      // VPN mode - both tests should pass ideally
      return vpnTestStatus === 'success' && apiTestStatus === 'success';
    }
    
    // Public IP mode - API test should pass
    return apiTestStatus === 'success';
  };


  const fetchWebhookStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(API_URL + '/api/v2/mikrotiks/webhook-status', {
        headers: { Authorization: 'Bearer ' + token },
        timeout: 60000,
      });
      if (r.data && r.data.success) {
        setWebhookStatus(r.data.data || {});
      }
    } catch (e) {
      // silent
    }
  };

  const fetchActiveCounts = async () => {
    setLoadingCounts(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/v2/mikrotiks/active-sessions-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.data?.success) {
        setActiveCounts(r.data.data || {});
      }
    } catch (e) {
      console.warn('[active-counts] fetch failed:', e.message);
    } finally {
      setLoadingCounts(false);
    }
  };

  const fetchRouters = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Check if token is valid format (JWT has 3 parts)
      if (!token || token.split('.').length !== 3) {
        console.error('Invalid token format');
        localStorage.clear();
        window.location.href = '/login';
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/v2/mikrotiks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const routersData = response.data.data || [];
        
        // Enrich routers with whitelist status
        const whitelistResponse = await axios.get(`${API_URL}/api/v2/ip-whitelist`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (whitelistResponse.data.success) {
          const whitelistMap = {};
          whitelistResponse.data.data.forEach(wl => {
            whitelistMap[wl.ip_address] = wl.is_active;
          });
          
          // Add whitelist_active flag to each router
          const enrichedRouters = routersData.map(router => {
            const ipInWhitelist = router.host in whitelistMap;
            const isActive = whitelistMap[router.host];
            
            return {
              ...router,
              whitelist_active: ipInWhitelist ? isActive : true // Show warning only if IP is in whitelist but not active
            };
          });
          
          setRouters(enrichedRouters);
        } else {
          setRouters(routersData);
        }
      }
    } catch (error) {
      console.error('Error fetching routers:', error);
      
      // Handle authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        localStorage.clear();
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else {
        toast.error('Gagal memuat data router');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPppoeApiKey = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API_URL}/api/mikrotik/pppoe-webhook-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.data && resp.data.success && resp.data.data && resp.data.data.api_key) {
        setPppoeApiKey(resp.data.data.api_key);
      }
    } catch (e) {
      console.warn('Could not fetch PPPoE webhook API key:', e.message);
    }
  };

  const fetchWhitelist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/ip-whitelist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setWhitelist(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching whitelist:', error);
      toast.error('Gagal memuat data whitelist');
    }
  };


  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setBranches(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };


  // 2026-05-31: fetch list of owners for the router assignment dropdown
  const fetchOwners = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/owners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = response.data?.data || response.data || [];
      const list = Array.isArray(payload) ? payload : (payload?.owners || payload?.items || []);
      setOwners(list);
    } catch (error) {
      console.error('Error fetching owners:', error);
    }
  };


  const validateIPAddress = (ip) => {
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipPattern.test(ip)) {
      return false;
    }
    const parts = ip.split('.');
    return parts.every(part => parseInt(part) >= 0 && parseInt(part) <= 255);
  };

  const handleAddWhitelist = async (e) => {
    e.preventDefault();
    
    // Validate IP address format
    if (!validateIPAddress(whitelistFormData.ip_address)) {
      toast.error('Format IP address tidak valid. Gunakan format: xxx.xxx.xxx.xxx');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      if (editingWhitelist) {
        // Update existing IP
        const response = await axios.put(
          `${API_URL}/api/v2/ip-whitelist/${editingWhitelist.id}`,
          whitelistFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success(response.data.message || 'IP berhasil diupdate');
          setShowAddWhitelistModal(false);
          setEditingWhitelist(null);
          setWhitelistFormData({ ip_address: '', description: '' });
          await fetchWhitelist();
        }
      } else {
        // Add new IP
        const response = await axios.post(
          `${API_URL}/api/v2/ip-whitelist`,
          whitelistFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          toast.success(response.data.message || 'IP berhasil ditambahkan ke whitelist');
          setShowAddWhitelistModal(false);
          setWhitelistFormData({ ip_address: '', description: '' });
          await fetchWhitelist();
        }
      }
    } catch (error) {
      console.error('Error saving whitelist:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan IP ke whitelist');
    } finally {
      setSaving(false);
    }
  };

  const handleEditWhitelist = (entry) => {
    setEditingWhitelist(entry);
    setWhitelistFormData({
      ip_address: entry.ip_address,
      description: entry.description || ''
    });
    setShowAddWhitelistModal(true);
  };

  const handleDeleteWhitelist = async () => {
    if (!whitelistToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/api/v2/ip-whitelist/${whitelistToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || 'IP berhasil dihapus dari whitelist');
        setShowDeleteWhitelistModal(false);
        setWhitelistToDelete(null);
        await fetchWhitelist();
      }
    } catch (error) {
      console.error('Error deleting from whitelist:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus IP dari whitelist');
    }
  };

  const handleToggleWhitelistStatus = async (whitelistId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/v2/ip-whitelist/${whitelistId}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(!currentStatus ? 'IP diaktifkan' : 'IP dinonaktifkan');
        await fetchWhitelist();
        await fetchRouters(); // Refresh routers to update warning indicators
      }
    } catch (error) {
      console.error('Error toggling whitelist status:', error);
      toast.error(error.response?.data?.detail || 'Gagal mengubah status IP');
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields - skip host validation if auto_create_vpn is enabled
    if (!formData.auto_create_vpn && (!safeTrim(formData.host))) {
      toast.error('Host/IP Address harus diisi!');
      return;
    }
    
    if (!safeTrim(formData.router_name)) {
      toast.error('Nama Router harus diisi!');
      return;
    }
    
    // AUTO-DETECT PORT if empty (skip when VPN auto-create is on)
    let portToUse = formData.port;
    const skipDetectByVpn = formData.auto_create_vpn || (formData.host && formData.host.startsWith('10.'));
    if ((!portToUse || portToUse === '' || portToUse === 0) && !skipDetectByVpn) {
      try {
        toast.info('Port kosong - mencoba auto-detect...');
        const token0 = localStorage.getItem('token');
        const detectResp = await axios.post(
          `${API_URL}/api/mikrotik/detect-api-port`,
          { host: formData.host },
          { headers: { Authorization: `Bearer ${token0}` } }
        );
        if (detectResp.data?.success && detectResp.data.port) {
          portToUse = detectResp.data.port;
          setFormData((prev) => ({ ...prev, port: portToUse }));
          toast.success(`✅ Auto-detect: Port ${portToUse} (${(detectResp.data.type || '').toUpperCase()}) terbuka`);
        } else {
          toast.error(detectResp.data?.message || 'Auto-detect gagal, isi Port manual');
          return;
        }
      } catch (errDet) {
        toast.error(`Auto-detect gagal: ${errDet.response?.data?.detail || errDet.message}. Isi Port manual.`);
        return;
      }
    }
    
    if (!portToUse || portToUse === '') {
      toast.error('Port API harus diisi!');
      return;
    }
    
    if (!safeTrim(formData.username)) {
      toast.error('Username harus diisi!');
      return;
    }

    // 2026-07-19: Untuk EDIT mode, password boleh kosong (biar backend
    // mempertahankan password lama). Cek password required HANYA saat CREATE.
    if (!editingRouter && !safeTrim(formData.password)) {
      toast.error('Password harus diisi!');
      return;
    }
    
    console.log('Form submitted with data:', { ...formData, password: formData.password ? '***' : 'EMPTY' });
    console.log('Host value:', formData.host);
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');

      // ==== VPN WIZARD (Mode A: push L2TP, Mode B: use existing VPN user) ====
      if (!editingRouter && formData.use_vpn_wizard && connectionType === 'vpn_tunnel') {
        const existingVpnUserId = formData.existing_vpn_user_id ? parseInt(formData.existing_vpn_user_id) : null;
        const tempHost = safeTrim(formData.host);
        if (!existingVpnUserId && !tempHost) {
          toast.error('Pilih VPN User existing ATAU isi Temporary Public IP MikroTik');
          setSaving(false);
          return;
        }
        if (!safeTrim(formData.username) || !safeTrim(formData.password)) {
          toast.error('Username & password API MikroTik wajib diisi');
          setSaving(false);
          return;
        }
        toast.info(existingVpnUserId
          ? '🪄 VPN Wizard (existing user) — push /radius via tunnel...'
          : '🪄 VPN Wizard berjalan — push L2TP via MikroTik API (mohon tunggu ~30-90 detik)...');
        try {
          const tenantResp = await axios.get(`${API_URL}/api/v2/tenant/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const tenantInfo = tenantResp.data?.data?.tenant || {};
          const tenantId = tenantInfo.id || tenantInfo.tenant_id;

          const selectedBranch = branches.find(b => String(b.id) === String(formData.branch_id));

          const wizardResp = await axios.post(
            `${API_URL}/api/v2/mikrotiks/setup-vpn-wizard`,
            {
              name: formData.router_name,
              tenant_id: tenantId,
              branch_id: formData.branch_id ? parseInt(formData.branch_id) : null,
              branch_name: selectedBranch ? selectedBranch.name : '',
              temp_public_host: existingVpnUserId ? '' : tempHost,
              existing_vpn_user_id: existingVpnUserId,
              api_user: formData.username,
              api_pass: formData.password,
              api_port: parseInt(formData.port) || 8728,
              location: formData.location || ''
            },
            { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
          );

          if (wizardResp.data?.success) {
            const d = wizardResp.data.data || {};
            toast.success(`✅ VPN Router onboarded! IP VPN: ${d.vpn_local_ip}`);
            console.log('VPN Wizard log:', d.log);
            setShowAddModal(false);
            setEditingRouter(null);
            resetForm();
            await fetchRouters();
          } else {
            toast.error('VPN Wizard gagal: ' + (wizardResp.data?.message || 'Unknown'));
          }
        } catch (wizErr) {
          console.error('VPN Wizard error:', wizErr);
          toast.error('VPN Wizard gagal: ' + (wizErr.response?.data?.detail || wizErr.message));
        } finally {
          setSaving(false);
        }
        return;
      }
      // ==== END VPN WIZARD branch ====
      
      // Prepare data - ensure host is always sent + resolve branch_name from branch_id
      const _selectedBranch = branches.find(b => String(b.id) === String(formData.branch_id));
      const submitData = {
        ...formData,
        port: portToUse,
        host: formData.auto_create_vpn ? 'pending-vpn-connection' : safeTrim(formData.host),
        branch_id: formData.branch_id ? parseInt(formData.branch_id) : null,
        branch_name: _selectedBranch ? _selectedBranch.name : (formData.branch_name || '')
      };

      // 2026-07-19: Saat EDIT — hapus field password sensitif yang kosong supaya
      // backend TIDAK meng-overwrite value existing dengan string kosong.
      // Ini fix bug: browser sering auto-fill password tapi user kadang sengaja
      // kosongkan (hanya buka form). Kalau tetap dikirim, password DB tertimpa.
      if (editingRouter) {
        if (!safeTrim(submitData.password)) delete submitData.password;
        if (!safeTrim(submitData.radius_shared_secret)) delete submitData.radius_shared_secret;
        if (!safeTrim(submitData.radius_db_password)) delete submitData.radius_db_password;
      }

      // Skip connection test if:
      // 1. auto_create_vpn is enabled
      // 2. host is VPN IP (10.x.x.x)
      // 3. radius_server_type is 'vpn' (VPN mode)
      // 4. host is 'pending-vpn-connection'
      const isVpnIp = submitData.host && submitData.host.startsWith('10.');
      const isVpnMode = formData.radius_server_type === 'vpn';
      const isPendingVpn = submitData.host === 'pending-vpn-connection';
      const shouldSkipTest = formData.auto_create_vpn || isVpnIp || isVpnMode || isPendingVpn;
      
      if (!shouldSkipTest) {
        // MANDATORY CONNECTION TEST BEFORE SAVE
        console.log('🔍 Testing connection before save...');
        toast.info('Testing koneksi router...');
        
        const testResponse = await axios.post(
          `${API_URL}/api/mikrotik/test-connection-direct`,
          {
            host: submitData.host,
            port: portToUse,
            username: submitData.username,
            password: submitData.password || formData.password || '',
            // 2026-07-19: Kirim router_id supaya backend bisa ambil password dari DB
            // kalau field password dikosongkan (user tidak ubah password).
            router_id: editingRouter ? editingRouter.id : undefined,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (!testResponse.data.success) {
          toast.error(`Koneksi gagal: ${testResponse.data.message}`);
          setSaving(false);
          return;
        }
        
        console.log('✅ Connection test passed, proceeding with save...');
        toast.success('Koneksi berhasil! Menyimpan router...');
      } else if (isVpnMode || isPendingVpn) {
        toast.info('Mode VPN - Simpan tanpa test koneksi. Pastikan VPN sudah terhubung.');
      }
      
      if (editingRouter) {
        // Update existing router
        console.log('Updating router:', editingRouter.id, 'with host:', submitData.host);
        const response = await axios.put(
          `${API_URL}/api/v2/mikrotiks/${editingRouter.id}`,
          {
            ...submitData,
            status: 'online',  // Set status online since connection test passed
            last_check: new Date().toISOString()
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Update response:', response.data);
        toast.success('Router berhasil diupdate dan status ONLINE');
      } else {
        // Check if auto_create_vpn is enabled and router doesn't have public IP
        let vpnData = null;
        if (formData.auto_create_vpn && !formData.has_public_ip) {
          toast.info('Membuat VPN otomatis...');
          try {
            // Get tenant info
            const tenantResponse = await axios.get(`${API_URL}/api/v2/tenant/profile`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const tenantInfo = tenantResponse.data?.data?.tenant || {};
            const tenantId = tenantInfo.id || tenantInfo.tenant_id;
            const tenantName = tenantInfo.company_name || tenantInfo.name || 'Tenant';
            
            // Create VPN
            const vpnResponse = await axios.post(
              `${API_URL}/api/v2/tenant-vpn/create?tenant_name=${encodeURIComponent(tenantName)}&tenant_id=${tenantId}`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (vpnResponse.data.success) {
              vpnData = vpnResponse.data.data;
              toast.success('VPN berhasil dibuat!');
              
              // Update submitData with VPN IP
              if (vpnData.vpn_username) {
                submitData.vpn_interface = `vpn-radius-${tenantId.substring(0, 8)}`;
              }
            } else {
              toast.warning('VPN gagal dibuat: ' + (vpnResponse.data.message || 'Unknown error'));
            }
          } catch (vpnError) {
            console.error('VPN creation error:', vpnError);
            toast.warning('VPN gagal dibuat: ' + (vpnError.response?.data?.detail || vpnError.message));
          }
        }
        
        // Add new router
        console.log('Adding new router with host:', submitData.host);
        const response = await axios.post(
          `${API_URL}/api/v2/mikrotiks`,
          {
            ...submitData,
            status: formData.auto_create_vpn ? 'pending_vpn' : 'online',
            last_check: new Date().toISOString()
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Add response:', response.data);
        toast.success(formData.auto_create_vpn 
          ? 'Router berhasil ditambahkan! Jalankan script VPN di MikroTik.' 
          : 'Router berhasil ditambahkan dengan status ONLINE');
        
        // If VPN was created, show the VPN data modal
        if (vpnData) {
          setVpnCreatedData(vpnData);
          // Don't close modal yet - show VPN script first
          return;
        }
      }
      
      setShowAddModal(false);
      setEditingRouter(null);
      resetForm();
      
      // Force refresh routers list
      console.log('Fetching updated routers list...');
      await fetchRouters();
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan router');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (router) => {
    setEditingRouter(router);
    setFormData({
      router_name: router.router_name,
      host: router.host,
      port: router.port,
      username: router.username,
      password: '', // Don't populate password for security
      location: router.location || '',
      branch_id: router.branch_id || '',
      owner_id: router.owner_id || '',
      branch_name: router.branch_name || '',
      // VPN fields
      has_public_ip: router.has_public_ip !== false, // default true
      // RADIUS fields
      connection_mode: router.connection_mode || 'api_only',
      radius_enabled: router.radius_enabled || false,
      radius_nas_identifier: router.radius_nas_identifier || '',
      radius_shared_secret: '', // Don't populate for security
      // Additional RADIUS fields
      radius_server_type: router.radius_server_type || 'public',
      radius_server_ip: router.radius_server_ip || '103.179.252.149',
      radius_vpn_ip: router.radius_vpn_ip || '10.10.10.1',
      radius_auth_port: router.radius_auth_port || 1812,
      radius_acct_port: router.radius_acct_port || 1813,
      radius_timeout: router.radius_timeout || 3000,
      radius_coa_port: router.radius_coa_port || 3799,
      // Multi-RADIUS Database config
      radius_host: router.radius_host || '',
      radius_port: router.radius_port || 3306,
      radius_db_user: router.radius_db_user || '',
      radius_db_password: '', // Don't populate for security
      radius_db_name: router.radius_db_name || 'radius'
    });
    setShowAddModal(true);
  };

  const handleDeleteClick = (router) => {
    console.log('Delete clicked for:', router.router_name);
    setRouterToDelete(router);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!routerToDelete) return;
    
    console.log('Confirming delete for:', routerToDelete.router_name);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/api/v2/mikrotiks/${routerToDelete.id}`, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Delete response:', response.data);
      toast.success(`Router "${routerToDelete.router_name}" berhasil dihapus`);
      
      setShowDeleteModal(false);
      setRouterToDelete(null);
      fetchRouters();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus router');
    }
  };

  const cancelDelete = () => {
    console.log('Delete cancelled');
    setShowDeleteModal(false);
    setRouterToDelete(null);
  };

  const handleRedeployIsolir = async (routerId, routerName) => {
    if (!window.confirm(`Re-deploy semua isolir rules (NAT + Filter + Masquerade) ke router "${routerName}"?\n\nRule lama dengan comment Isolir-* akan di-replace.`)) {
      return;
    }
    setRedeployingRouter(routerId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/mikrotik/setup-isolir-profile`,
        { router_id: String(routerId) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data || {};
      if (data.success) {
        toast.success(`Isolir rules berhasil di-deploy ke ${routerName}`, { duration: 5000 });
        // Show detailed router_results if available
        if (Array.isArray(data.router_results) && data.router_results.length > 0) {
          console.log('[REDEPLOY-ISOLIR]', data.router_results);
        }
      } else {
        const msg = data.message || data.detail || 'Re-deploy gagal';
        toast.error(`${routerName}: ${msg}`, { duration: 8000 });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Error';
      toast.error(`Re-deploy gagal pada ${routerName}: ${msg}`, { duration: 8000 });
    } finally {
      setRedeployingRouter(null);
    }
  };

  const handleTestConnection = async (routerId) => {
    console.log('Testing connection for router:', routerId);
    setTestingRouter(routerId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/mikrotiks/${routerId}/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Test response:', response.data);
      
      if (response.data.success) {
        toast.success('Koneksi berhasil! Router online.');
        // Show platform/version info if available
        if (response.data.data?.platform) {
          console.log('Router info:', response.data.data);
        }
      } else {
        // Show detailed error message with troubleshooting guide
        const errorMessage = response.data.message || 'Unknown error';
        const troubleshooting = response.data.troubleshooting;
        
        if (troubleshooting) {
          // Show error with detailed troubleshooting guide
          toast.error(
            <div className="space-y-2">
              <div className="font-semibold">{errorMessage}</div>
              <div className="text-sm whitespace-pre-line border-t pt-2 mt-2">
                {troubleshooting}
              </div>
            </div>,
            { duration: 10000 } // Show for 10 seconds
          );
        } else {
          toast.error(errorMessage);
        }
      }
      
      // Refresh routers list to get updated status
      console.log('Fetching updated router status...');
      await fetchRouters();
      
    } catch (error) {
      console.error('Test connection error:', error);
      toast.error(error.response?.data?.detail || 'Gagal test koneksi');
      
      // Still refresh to get any status update from backend
      await fetchRouters();
    } finally {
      setTestingRouter(null);
    }
  };


  const handleAutoSetupIsolir = async () => {
    if (!isolirSetupRouter) return;

    setIsolirSetupLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Step 1: Setup PPP Profile ISOLIR (pool + profile)
      toast.info('Membuat PPP Profile ISOLIR...');
      const profileResponse = await axios.post(
        `${API_URL}/api/mikrotik/setup-isolir-profile`,
        {
          pool_range: "10.99.99.2-10.99.99.254",
          local_address: "10.99.99.1",
          rate_limit: "256k/256k",
          dns_server: "10.99.99.1"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (profileResponse.data.success) {
        toast.success('PPP Profile ISOLIR berhasil dibuat!');
      }
      
      // Step 2: Setup Firewall Rules (NAT, Filter)
      toast.info('Mengkonfigurasi Firewall Rules...');
      const response = await axios.post(
        `${API_URL}/api/v2/mikrotiks/${isolirSetupRouter.id}/auto-setup-isolir`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message || 'Konfigurasi isolir berhasil diterapkan!');
        setShowIsolirSetupModal(false);
        setIsolirSetupRouter(null);
      }
    } catch (error) {
      console.error('Auto-setup isolir error:', error);
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Gagal mengkonfigurasi isolir');
    } finally {
      setIsolirSetupLoading(false);
    }
  };

  const openIsolirSetupModal = (router) => {
    setIsolirSetupRouter(router);
    setShowIsolirSetupModal(true);
  };

  // Per-Router: Sync IP Pools + PPPoE Profiles from DB to MikroTik
  // (Zero-config onboarding for a freshly registered router.)
  const handleSyncRouterProfiles = async (router) => {
    if (!window.confirm(`Sync semua IP Pool & Profile PPPoE ke router "${router.router_name || router.name}"?\n\nSemua service plan aktif tenant ini akan dibuatkan profile di MikroTik.`)) {
      return;
    }
    setSyncRouterProfilesLoading(router.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/mikrotiks/${router.id}/sync-profiles`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const d = response.data.data || {};
        const msg = `Profile sync OK!\n` +
          `Pools: ${d.pools_created?.length || 0} dibuat, ${d.pools_updated?.length || 0} diupdate\n` +
          `Profiles: ${d.profiles_created?.length || 0} dibuat, ${d.profiles_updated?.length || 0} diupdate` +
          (d.errors?.length ? `\nErrors: ${d.errors.length}` : '');
        toast.success(msg, { duration: 7000 });
        console.log('Sync result:', d);
      } else {
        toast.error(response.data.message || 'Sync gagal');
      }
    } catch (error) {
      console.error('Sync router profiles error:', error);
      const errorMsg = error.response?.data?.detail || 'Gagal sync profile ke MikroTik';
      toast.error(errorMsg);
    } finally {
      setSyncRouterProfilesLoading(null);
    }
  };

  // Sync RADIUS Now: refresh online status based on flag R (radius=true)
  const handleSyncRadiusNow = async (router) => {
    setSyncRadiusLoading(router.id);
    try {
      // apiClient auto-attaches Bearer token from localStorage('token')
      const response = await apiClient.post(`/api/v2/mikrotiks/${router.id}/sync-radius-now`, {});
      if (response.data?.success) {
        const r = response.data;
        toast.success(
          `Sync RADIUS ${router.name}: ${r.radius_count}/${r.total_active} flag R (${r.local_count} flag L). ` +
          `${r.customers_set_online} customer di-set Online.`,
          { duration: 6000 }
        );
        // refresh router list to update active_pppoe_count badge
        fetchRouters();
      } else {
        toast.error(`Gagal sync RADIUS: ${response.data?.detail || "Unknown error"}`);
      }
    } catch (error) {
      toast.error(`Gagal sync RADIUS: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSyncRadiusLoading(null);
    }
  };

  // Global sync ALL routers at once
  const handleSyncRadiusNowAll = async () => {
    if (syncAllLoading) return;
    if (!window.confirm('Sinkron RADIUS untuk SEMUA router sekarang? (proses ~10-30 detik tergantung jumlah router)')) return;
    setSyncAllLoading(true);
    const t = toast.loading('Menyinkronkan semua router...');
    try {
      const response = await apiClient.post('/api/v2/mikrotiks/sync-radius-now-all', {});
      if (response.data?.success) {
        const agg = response.data.aggregate || {};
        toast.success(
          `Sinkron selesai: ${agg.ok}/${response.data.total_routers} router OK, ${agg.failed} gagal, ${agg.customers_set_online} customer online.`,
          { id: t, duration: 8000 }
        );
        const failed = (response.data.results || []).filter(r => r.status === 'error');
        if (failed.length) {
          setTimeout(() => {
            failed.slice(0, 5).forEach(f => {
              toast.error(`${f.router_name}: ${f.error}`, { duration: 6000 });
            });
          }, 500);
        }
        fetchRouters();
      } else {
        toast.error(`Gagal sync semua: ${response.data?.detail || 'Unknown error'}`, { id: t });
      }
    } catch (error) {
      toast.error(`Gagal sync semua: ${error.response?.data?.detail || error.message}`, { id: t });
    } finally {
      setSyncAllLoading(false);
    }
  };

  // ===== 2026-05-28: Move Customers Between Routers Wizard =====
  const openMoveCustModal = async (router) => {
    setMoveSourceRouter(router);
    setMoveTargetRouterId('');
    setMoveCustomers([]);
    setMoveSelectedIds(new Set());
    setMoveSearch('');
    setMoveResult(null);
    setShowMoveCustModal(true);
    setMoveLoadingList(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(
        `${API_URL}/api/v2/mikrotiks/${router.id}/customers-preview?limit=5000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data?.success) {
        setMoveCustomers(resp.data.data || []);
      } else {
        toast.error('Gagal memuat daftar customer router sumber');
      }
    } catch (e) {
      toast.error(`Gagal memuat customer: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMoveLoadingList(false);
    }
  };

  const closeMoveCustModal = () => {
    if (moveExecuting) return;
    setShowMoveCustModal(false);
    setMoveSourceRouter(null);
    setMoveSelectedIds(new Set());
    setMoveCustomers([]);
    setMoveResult(null);
  };

  const toggleMoveSelect = (cid) => {
    setMoveSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  // 2026-05-28: Apply global branch/router filter from AppContext.
  // 'all' / '' / null means no filter (show everything).
  const filteredRouters = (routers || []).filter((r) => {
    if (selectedBranch && selectedBranch !== 'all') {
      if (String(r.branch_id || '') !== String(selectedBranch)) return false;
    }
    if (selectedRouter && selectedRouter !== 'all') {
      if (String(r.id) !== String(selectedRouter)) return false;
    }
    return true;
  });

  const filteredMoveCustomers = moveCustomers.filter((c) => {
    if (!moveSearch.trim()) return true;
    const q = moveSearch.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.pppoe_username || '').toLowerCase().includes(q) ||
      String(c.id).includes(q)
    );
  });

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredMoveCustomers.map((c) => c.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => moveSelectedIds.has(id));
    setMoveSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const executeMoveCustomers = async () => {
    if (!moveSourceRouter || !moveTargetRouterId) {
      toast.error('Pilih router tujuan terlebih dahulu');
      return;
    }
    if (String(moveTargetRouterId) === String(moveSourceRouter.id)) {
      toast.error('Router tujuan harus berbeda dengan router sumber');
      return;
    }
    const ids = Array.from(moveSelectedIds);
    if (ids.length === 0) {
      toast.error('Pilih minimal 1 customer untuk dipindahkan');
      return;
    }
    const targetRouter = routers.find((r) => String(r.id) === String(moveTargetRouterId));
    const tgtName = targetRouter?.name || targetRouter?.router_name || `#${moveTargetRouterId}`;
    if (!window.confirm(
      `Pindahkan ${ids.length} pelanggan dari "${moveSourceRouter.name}" ke "${tgtName}"?

` +
      `Setelah dipindah, customer akan re-login PPPoE pada router tujuan. Pastikan IP Pool & profile di router tujuan sudah tersedia.`
    )) return;

    setMoveExecuting(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API_URL}/api/v2/mikrotiks/move-customers`,
        {
          source_router_id: moveSourceRouter.id,
          target_router_id: Number(moveTargetRouterId),
          customer_ids: ids,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data?.success) {
        setMoveResult(resp.data.data);
        toast.success(resp.data.message || 'Berhasil memindahkan customer', { duration: 7000 });
        fetchRouters();
      } else {
        toast.error(resp.data?.detail || 'Gagal memindahkan customer');
      }
    } catch (e) {
      toast.error(`Gagal memindahkan: ${e.response?.data?.detail || e.message}`);
    } finally {
      setMoveExecuting(false);
    }
  };

  // Setup Webhook on MikroTik
  const handleSetupWebhook = async (router) => {
    setWebhookSetupLoading(router.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/mikrotik/setup-webhook/${router.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || 'Webhook script berhasil dipasang!');
      } else {
        toast.error(response.data.message || 'Gagal setup webhook');
      }
    } catch (error) {
      console.error('Webhook setup error:', error);
      const errorMsg = error.response?.data?.detail || 'Gagal setup webhook script';
      toast.error(errorMsg);
    } finally {
      setWebhookSetupLoading(null);
    }
  };

  // === COPY RADIUS SECRETS -> MIKROTIK ===
  // Step 1: open modal and request preview (dry-run)
  const handleCopySecretsPreview = async (router) => {
    setCopySecretsLoading(router.id);
    setCopySecretsModal({ open: true, router, preview: null, executing: false, result: null });
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API_URL}/api/v2/mikrotik/${router.id}/copy-radius-secrets/preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data?.success) {
        setCopySecretsModal(prev => ({ ...prev, preview: resp.data.data }));
      } else {
        toast.error(resp.data?.message || 'Gagal preview');
        setCopySecretsModal({ open: false, router: null, preview: null, executing: false, result: null });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal preview copy-secrets');
      setCopySecretsModal({ open: false, router: null, preview: null, executing: false, result: null });
    } finally {
      setCopySecretsLoading(null);
    }
  };

  // Step 2: actually execute the copy
  const handleCopySecretsExecute = async () => {
    const router = copySecretsModal.router;
    if (!router) return;
    setCopySecretsModal(prev => ({ ...prev, executing: true }));
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API_URL}/api/v2/mikrotik/${router.id}/copy-radius-secrets/execute`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 }
      );
      if (resp.data?.success) {
        const d = resp.data.data || {};
        toast.success(`✅ ${d.to_add_or_added} secret berhasil ditambahkan ke ${router.name || router.router_name} (sudah ada: ${d.already_exists}, error: ${d.errors_count})`);
        setCopySecretsModal(prev => ({ ...prev, executing: false, result: d }));
      } else {
        toast.error(resp.data?.message || 'Eksekusi gagal');
        setCopySecretsModal(prev => ({ ...prev, executing: false }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal eksekusi copy-secrets');
      setCopySecretsModal(prev => ({ ...prev, executing: false }));
    }
  };

  // Step 3 (opsional): Cleanup — hapus semua secret bermarker [COPY-FROM-RADIUS]
  const handleCopySecretsCleanup = async () => {
    const router = copySecretsModal.router;
    if (!router) return;
    if (!window.confirm(`Yakin hapus SEMUA secret bertanda [COPY-FROM-RADIUS] di router ${router.name || router.router_name}? Aksi ini tidak bisa di-undo.`)) return;
    setCopySecretsModal(prev => ({ ...prev, executing: true }));
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API_URL}/api/v2/mikrotik/${router.id}/copy-radius-secrets/cleanup`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 }
      );
      if (resp.data?.success) {
        const d = resp.data.data || {};
        toast.success(`🗑️ ${d.deleted} secret [COPY-FROM-RADIUS] terhapus (error: ${d.errors_count})`);
        // Refresh preview by re-calling preview
        setCopySecretsModal(prev => ({ ...prev, preview: null, result: null, executing: false }));
        await handleCopySecretsPreview(router);
      } else {
        toast.error(resp.data?.message || 'Cleanup gagal');
        setCopySecretsModal(prev => ({ ...prev, executing: false }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal cleanup');
      setCopySecretsModal(prev => ({ ...prev, executing: false }));
    }
  };

// Generate MikroTik setup scripts
  const generateMikroTikScripts = (router) => {
    // FIX: For VPN routers, RADIUS server IP is ALWAYS 10.10.10.1 (the
    // L2TP gateway on the billing VPS). router.radius_vpn_ip is the
    // MikroTik's OWN tunnel IP (used for src-address), NOT the RADIUS
    // server. Swapping them caused MikroTik to send RADIUS requests to
    // itself, which timed out and broke PPPoE auth.
    const VPN_RADIUS_GATEWAY = '10.10.10.1';
    const PUBLIC_BILLING_IP = '103.179.252.149';
    const radiusIp = router.radius_server_type === 'vpn'
      ? VPN_RADIUS_GATEWAY
      : (router.radius_server_ip || PUBLIC_BILLING_IP);
    // Isolir dual-IP (2026-07-18): Redirect via PUBLIC (paling andal),
    // Masquerade + Filter Accept-Server dibuat untuk KEDUA IP (VPN + Public)
    // agar fallback tetap jalan meskipun VPN L2TP down.
    const ISOLIR_REDIRECT_IP = PUBLIC_BILLING_IP;
    // Prefer the secret persisted in DB (radius_secret column) - kept in sync
    // with FreeRADIUS NAS table by backend. NEVER fall back to a placeholder
    // string here: MikroTik will accept any text as secret literally, causing
    // a silent secret mismatch with FreeRADIUS (radius timeout). Instead, if
    // the secret is missing we emit an explicit error script so the admin
    // notices and clicks "Edit Router" -> Save (backend will auto-generate).
    const radiusSecret = (router.radius_secret || router.radius_shared_secret || '').trim();
    if (!radiusSecret) {
      const errMsg = `# !!! ERROR: radius_secret kosong untuk router ${router.router_name || ''} (id=${router.id || '?'}).\n# Buka menu "Edit Router" lalu klik "Simpan" - backend akan auto-generate\n# secret + sync ke FreeRADIUS NAS otomatis, lalu klik "Generate Script" ulang.`;
      return { full: errMsg, radius: errMsg, isolir: errMsg, hotspot: errMsg };
    }
    const authPort = router.radius_auth_port || 1812;
    const acctPort = router.radius_acct_port || 1813;
    const coaPort = router.radius_coa_port || 3799;
    const webhookUrl = `${window.location.origin}/api/webhooks/payment`;
    
    // Determine src-address based on connection type
    const srcAddress = router.radius_server_type === 'vpn' 
      ? (router.vpn_ip || router.radius_vpn_ip || '') 
      : (router.host || '');

    // v2_2026_02 HA_BACKUP_RADIUS: Emit backup RADIUS entry for BOTH router types:
    //   - VPN routers: backup via 10.10.11.1 (VPN 2 tunnel to VM 101)
    //   - Public routers: backup via 103.179.252.26 (VM 101 public IP)
    // Both use SAME shared secret because radius.nas is MariaDB-replicated S1 → VM 101,
    // so the same NAS entry (per router public IP) exists on both servers with same secret.
    const BACKUP_RADIUS_GATEWAY_VPN = '10.10.11.1';
    const BACKUP_RADIUS_PUBLIC_IP = '103.179.252.26';
    const isVpnRouter = router.radius_server_type === 'vpn';
    const backupRadiusIp = isVpnRouter ? BACKUP_RADIUS_GATEWAY_VPN : BACKUP_RADIUS_PUBLIC_IP;
    const backupSrcLine = (isVpnRouter && srcAddress) ? `
    src-address=${srcAddress} \\` : '';
    const backupRadiusBlock = `
/radius add service=ppp,hotspot address=${backupRadiusIp} secret=${radiusSecret} \\
    authentication-port=${authPort} accounting-port=${acctPort} \\${backupSrcLine}
    timeout=2s \\
    comment="RadiusLink RADIUS BACKUP (${isVpnRouter ? 'VPN 2' : 'Public VM 101'} - HA failover)"`;

    const scripts = {
      full: `# ============================================
# RADIUSLINK - SETUP SCRIPT
# Router: ${router.router_name}
# Generated: ${new Date().toLocaleString('id-ID')}
# ============================================
# Script ini aman dijalankan ulang.
# ============================================

# ====== 1. RADIUS Setup (DUAL HA — Primary + Backup) ======
:do { /radius remove [find comment~"RadiusLink"] } on-error={}
/radius add service=ppp,hotspot address=${radiusIp} secret=${radiusSecret} \\
    authentication-port=${authPort} accounting-port=${acctPort} \\${srcAddress ? `
    src-address=${srcAddress} \\` : ''}
    timeout=2s \\
    comment="RadiusLink RADIUS PRIMARY (${isVpnRouter ? 'VPN 1' : 'Public Server 1'})"${backupRadiusBlock}
/ppp aaa set use-radius=yes accounting=yes interim-update=1m
/radius incoming set accept=yes port=${coaPort}
:do { /interface pppoe-server server set [find] authentication=mschap2,mschap1,chap,pap } on-error={}

# ====== 2. Hotspot RADIUS (jika ada) ======
:do { /ip hotspot profile set [find] use-radius=yes } on-error={}

# ====== 3. Auto Isolir Setup (NAT + Filter) ======
# Hapus rules isolir lama
:do { /ip firewall nat remove [find comment~"RadiusLink-Isolir"] } on-error={}
:do { /ip firewall nat remove [find comment~"Isolir-"] } on-error={}
:do { /ip firewall filter remove [find comment~"Isolir-"] } on-error={}

# -- NAT Rules --
:: Isolir-DNS-Redirect
/ip firewall nat add chain=dstnat src-address-list=isolir dst-port=53 protocol=udp action=dst-nat to-addresses=8.8.8.8 to-ports=53 comment="Isolir-DNS-Redirect"

:: Isolir-HTTP-Redirect (target IP publik, paling andal — jalan walau VPN L2TP down)
/ip firewall nat add chain=dstnat src-address-list=isolir dst-port=80 protocol=tcp action=dst-nat to-addresses=${ISOLIR_REDIRECT_IP} to-ports=80 comment="Isolir-HTTP-Redirect"

:: Isolir-HTTPS-Redirect (target IP publik)
/ip firewall nat add chain=dstnat src-address-list=isolir dst-port=443 protocol=tcp action=dst-nat to-addresses=${ISOLIR_REDIRECT_IP} to-ports=80 comment="Isolir-HTTPS-Redirect"

:: NAT for VPN to Billing Server
:do { /ip firewall nat add chain=srcnat out-interface=vpn-radius action=masquerade comment="NAT for VPN to Billing Server" } on-error={}

:: Isolir-Masquerade-to-Server-Public (fallback via IP publik)
/ip firewall nat add chain=srcnat src-address-list=isolir dst-address=${PUBLIC_BILLING_IP} action=masquerade comment="Isolir-Masquerade-to-Server-Public"

:: Isolir-Masquerade-to-Server-VPN (jalur VPN L2TP internal)
/ip firewall nat add chain=srcnat src-address-list=isolir dst-address=${VPN_RADIUS_GATEWAY} action=masquerade comment="Isolir-Masquerade-to-Server-VPN"

# -- Filter Rules --
:: Isolir-Accept-DNS
/ip firewall filter add chain=forward src-address-list=isolir protocol=udp dst-port=53 action=accept comment="Isolir-Accept-DNS"

:: Isolir-Accept-Server-Public (allow reach billing via IP publik)
/ip firewall filter add chain=forward src-address-list=isolir dst-address=${PUBLIC_BILLING_IP} action=accept comment="Isolir-Accept-Server-Public"

:: Isolir-Accept-Server-VPN (allow reach billing via VPN)
/ip firewall filter add chain=forward src-address-list=isolir dst-address=${VPN_RADIUS_GATEWAY} action=accept comment="Isolir-Accept-Server-VPN"

:: Isolir-Drop-All
/ip firewall filter add chain=forward src-address-list=isolir action=drop comment="Isolir-Drop-All"

# ====== 3b. Auto-Sync Address-List Isolir dari Pool (CRITICAL) ======
# RADIUS mengirim Framed-Pool=pool-isolir yang memberikan IP dari range
# pool tersebut ke user isolir. Filter Isolir-Drop-All di atas match hanya
# src-address-list=isolir. Tanpa entry di address-list yang cover range
# pool-isolir, user isolir dapat IP dari pool tapi TIDAK match address-list
# — akibatnya rule drop tidak apply dan user tetap punya internet.
# Block ini auto-detect range pool-isolir di router dan sync ke address-list.
:do { /ip firewall address-list remove [find list=isolir comment~"Pool Isolir Range .auto."] } on-error={}
:if ([:len [/ip pool find name="pool-isolir"]] > 0) do={
    :local poolRanges [/ip pool get [find name="pool-isolir"] ranges]
    :put ("[ISOLIR-AUTOSYNC] pool-isolir ranges detected: " . $poolRanges)
    :foreach r in=[:toarray $poolRanges] do={
        :do {
            /ip firewall address-list add list=isolir address=$r comment="Pool Isolir Range (auto)"
            :put ("[ISOLIR-AUTOSYNC] added: address-list isolir <- " . $r)
        } on-error={ :put ("[ISOLIR-AUTOSYNC] add-fail: " . $r) }
    }
} else={
    :put "[ISOLIR-AUTOSYNC] WARNING: pool-isolir NOT FOUND di router ini. Buat pool 'pool-isolir' dulu sebelum re-run script agar mekanisme isolir berjalan."
}

# ====== 4. PPPoE Realtime Webhook (Event-Driven Architecture) ======
# Webhook PPPoE on-up/on-down OTOMATIS di-install ke setiap PPPoE profile
# oleh backend RadiusLink begitu router ini terdaftar/diupdate di UI.
# - Endpoint event  : ${window.location.origin}/api/mikrotik/pppoe-event
# - API Key tersimpan di system_settings.pppoe_webhook_api_key
# - Mode arsitektur : 100% event-driven, NOL polling MikroTik dari server.
# Cek status webhook lewat tombol "Re-deploy Webhook" di kartu router (UI).
# Atau panggil manual: POST ${window.location.origin}/api/mikrotik/setup-webhook/<router_id>

:put "========================================"
:put "  SETUP SELESAI!"
:put "========================================"
:put "RADIUS Primary  : ${radiusIp}"
:put "RADIUS Backup   : ${backupRadiusIp} (HA failover via ${isVpnRouter ? 'VPN 2' : 'Public VM 101'})"${srcAddress ? `
:put "Source Address  : ${srcAddress}"` : ''}
:put "Payment Webhook : ${webhookUrl}"
:put "PPPoE Event URL : ${window.location.origin}/api/mikrotik/pppoe-event"
:put "Architecture    : EVENT-DRIVEN (no polling, realtime via on-up/on-down)"
:put "PPPoE Webhook   : auto-install via UI 'Re-deploy Webhook' button"
:put "Verify HA       : /radius print  (harus tampil 2 baris — Primary + Backup)"
:put "========================================"`
    };

    return scripts;
  };

  const resetForm = () => {
    setFormData({
      router_name: '',
      host: '',
      port: '',
      username: '',
      password: '',
      location: '',
      branch_id: '',
      branch_name: '',
      has_public_ip: true,
      vpn_ip: '',
      vpn_interface: 'l2tp-billing',
      auto_create_vpn: false,
      use_vpn_wizard: false,
      temp_public_host: '',
      existing_vpn_user_id: '',
      connection_mode: 'api_only',
      radius_enabled: false,
      radius_nas_identifier: '',
      radius_shared_secret: 'testing123',
      // Additional RADIUS fields
      radius_server_type: 'public',
      radius_server_ip: '103.179.252.149',
      radius_vpn_ip: '10.10.10.1',
      radius_auth_port: 1812,
      radius_acct_port: 1813,
      radius_timeout: 3000,
      radius_coa_port: 3799,
      // Multi-RADIUS Database config
      radius_host: '',
      radius_port: 3306,
      radius_db_user: '',
      radius_db_password: '',
      radius_db_name: 'radius'
    });
    setSaving(false);
    setConnectionType(null); // Reset connection type selection
  };

  const handleAutoSync = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mikrotik/auto-sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message || 'Auto-sync routers berhasil!');
        await fetchRouters(); // Refresh router list
      } else {
        toast.error(response.data.message || 'Auto-sync gagal');
      }
    } catch (error) {
      console.error('Auto-sync error:', error);
      toast.error(error.response?.data?.detail || 'Gagal melakukan auto-sync');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (routerId) => {
    setShowPassword(prev => ({
      ...prev,
      [routerId]: !prev[routerId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-300">Memuat data router...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-1 dark:text-slate-100">Konfigurasi MikroTik</h1>
        <p className="text-sm text-gray-600 dark:text-slate-300">Kelola multiple MikroTik routers & IP Whitelist</p>
      </div>

      {/* Header dengan Auto-Sync Button */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <nav className="grid grid-cols-3 gap-1 sm:flex sm:gap-0 sm:space-x-8 w-full sm:w-auto">
          {isSuperAdmin && (
          <button
            onClick={() => setActiveTab('whitelist')}
            className={`min-w-0 py-2 px-1 border-b-2 font-medium text-[11px] sm:text-sm ${
              activeTab === 'whitelist'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center gap-1 sm:gap-2">
              <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="truncate">IP Whitelist</span>
              <Badge variant="secondary" className="text-[10px] sm:text-xs px-1.5 py-0">{whitelist.length}</Badge>
            </div>
          </button>
          )}
          <button
            onClick={() => setActiveTab('routers')}
            className={`min-w-0 py-2 px-1 border-b-2 font-medium text-[11px] sm:text-sm ${
              activeTab === 'routers'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center gap-1 sm:gap-2">
              <Server className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="truncate"><span className="sm:hidden">Router</span><span className="hidden sm:inline">Router MikroTik</span></span>
              <Badge variant="secondary" className="text-[10px] sm:text-xs px-1.5 py-0">{filteredRouters.length}</Badge>
            </div>
          </button>
          {isSuperAdmin && (
          <button
            onClick={() => setActiveTab('backup')}
            className={`min-w-0 py-2 px-1 border-b-2 font-medium text-[11px] sm:text-sm ${
              activeTab === 'backup'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center gap-1 sm:gap-2">
              <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="truncate"><span className="sm:hidden">Backup</span><span className="hidden sm:inline">Backup Secrets</span></span>
              <Badge variant="outline" className="hidden sm:inline-flex bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300">Failover</Badge>
            </div>
          </button>
          )}
        </nav>
        
        {/* Auto-Sync Controls */}
        {activeTab === 'routers' && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleAutoSync}
              disabled={loading}
              className="flex items-center justify-center gap-1.5 sm:gap-2 flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3 min-w-0"
            >
              <RefreshCw className={`h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 ${loading ? 'animate-spin' : ''}`} />
              <span className="truncate"><span className="sm:hidden">Auto-Sync</span><span className="hidden sm:inline">Auto-Sync Routers</span></span>
            </Button>
            <Button
              variant="secondary"
              onClick={handleSyncRadiusNowAll}
              disabled={syncAllLoading}
              data-testid="sync-radius-all-btn"
              title="Sinkron flag R (RADIUS) untuk SEMUA router sekaligus"
              className="flex items-center justify-center gap-1.5 sm:gap-2 flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3 min-w-0 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <RefreshCw className={`h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 ${syncAllLoading ? 'animate-spin' : ''}`} />
              <span className="truncate">{syncAllLoading ? 'Menyinkron...' : (<><span className="sm:hidden">Sync RADIUS</span><span className="hidden sm:inline">Sinkron Semua RADIUS</span></>)}</span>
            </Button>
            <Button
              data-testid="add-router-btn"
              onClick={() => {
                setEditingRouter(null);
                resetForm();
                setShowAddModal(true);
              }}
              className="flex items-center justify-center gap-1.5 sm:gap-2 flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3 min-w-0"
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="truncate"><span className="sm:hidden">Tambah</span><span className="hidden sm:inline">Tambah Router</span></span>
            </Button>
          </div>
        )}
        
        {activeTab === 'whitelist' && (
          <Button
            data-testid="add-whitelist-btn"
            onClick={() => {
              setEditingWhitelist(null);
              setWhitelistFormData({ ip_address: '', description: '' });
              setShowAddWhitelistModal(true);
            }}
            className="flex items-center justify-center gap-1.5 sm:gap-2 w-full sm:w-auto text-xs sm:text-sm px-3"
          >
            <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
            <span className="truncate"><span className="sm:hidden">Tambah IP</span><span className="hidden sm:inline">Tambah IP</span></span>
          </Button>
        )}
        
        {activeTab === 'backup' && (
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Button
              onClick={handleSyncBackupSecrets}
              disabled={backupSyncing}
              className="flex items-center justify-center gap-1.5 sm:gap-2 flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3 min-w-0 bg-blue-600 hover:bg-blue-700"
            >
              {backupSyncing ? <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 animate-spin" /> : <Database className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />}
              <span className="truncate">{backupSyncing ? 'Syncing...' : (<><span className="sm:hidden">Sync Pelanggan</span><span className="hidden sm:inline">Sync Semua Pelanggan</span></>)}</span>
            </Button>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'routers' ? (
        <>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1 dark:text-slate-300">Total Routers</p>
                <p className="text-2xl font-bold">{filteredRouters.length}</p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-lg dark:bg-indigo-900/40">
                <Server className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1 dark:text-slate-300">Online</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {filteredRouters.filter(r => r.status === 'online').length}
                </p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg dark:bg-green-900/40">
                <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1 dark:text-slate-300">Offline</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {filteredRouters.filter(r => r.status !== 'online').length}
                </p>
              </div>
              <div className="p-2 bg-red-100 rounded-lg dark:bg-red-900/40">
                <X className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Routers List */}
      {filteredRouters.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Server className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-slate-100">Belum Ada Router</h3>
            <p className="text-sm text-gray-500 mb-4 dark:text-slate-400">
              Tambahkan router MikroTik pertama Anda untuk memulai
            </p>
            <Button
              onClick={() => {
                setEditingRouter(null);
                resetForm();
                setShowAddModal(true);
              }}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Tambah Router Pertama
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRouters.map((router) => (
            <Card key={router.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Server className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    <CardTitle className="text-base">{router.router_name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {activeCounts[String(router.id)] && (() => {
                      const ac = activeCounts[String(router.id)];
                      const pppoe = ac.pppoe_count ?? 0;
                      const hotspot = ac.hotspot_count ?? 0;
                      const staticCnt = ac.static_count ?? 0;
                      if (ac.status !== 'online') {
                        return (
                          <Badge
                            data-testid={`active-sessions-count-${router.id}`}
                            title={`Router unreachable: ${ac.error || 'offline'}`}
                            className="bg-red-50 text-red-700 border border-red-200"
                          >✗ offline</Badge>
                        );
                      }
                      // 2026-07-31: click-through — pilih router di context + navigate ke Pelanggan filter online
                      const openCustomers = (type) => {
                        try { setSelectedRouter(String(router.id)); } catch(e) { /* noop */ }
                        try { localStorage.setItem('selectedRouter', String(router.id)); } catch(e) { /* noop */ }
                        navigate(`/app/customers/${type}?status=online`);
                      };
                      return (
                        <div className="flex items-center gap-1" data-testid={`active-sessions-count-${router.id}`}>
                          <button
                            type="button"
                            onClick={() => openCustomers('pppoe')}
                            title={`${pppoe} PPPoE sessions — klik untuk lihat pelanggan`}
                            data-testid={`badge-pppoe-${router.id}`}
                            className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-semibold text-xs hover:bg-emerald-200 transition-colors cursor-pointer"
                          >
                            {pppoe} PPP
                          </button>
                          <button
                            type="button"
                            onClick={() => openCustomers('hotspot')}
                            title={`${hotspot} Hotspot sessions — klik untuk lihat pelanggan`}
                            data-testid={`badge-hotspot-${router.id}`}
                            className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 font-semibold text-xs hover:bg-amber-200 transition-colors cursor-pointer"
                          >
                            {hotspot} HS
                          </button>
                          {staticCnt > 0 && (
                            <button
                              type="button"
                              onClick={() => openCustomers('dhcp')}
                              title={`${staticCnt} Static/DHCP online — klik untuk lihat pelanggan`}
                              data-testid={`badge-static-${router.id}`}
                              className="inline-flex items-center px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 font-semibold text-xs hover:bg-indigo-200 transition-colors cursor-pointer"
                            >
                              {staticCnt} ST
                            </button>
                          )}
                          {(() => {
                            // 2026-07-31: Clock-Drift Warning — kalau MikroTik timezone drift > 2h
                            const driftMin = ac.clock_drift_min ?? 0;
                            if (Math.abs(driftMin) < 120) return null;
                            const hrs = (driftMin / 60);
                            const sign = driftMin > 0 ? '+' : '';
                            const label = `${sign}${hrs.toFixed(1)}h`;
                            const dir = driftMin > 0 ? 'ke DEPAN' : 'ke BELAKANG';
                            return (
                              <span
                                data-testid={`clock-drift-${router.id}`}
                                title={`Clock-Drift: jam MikroTik ${dir} ${Math.abs(hrs).toFixed(1)} jam dari server. Silakan set TZ MikroTik ke Asia/Jakarta (/system clock set time-zone-name=Asia/Jakarta) supaya session accounting akurat.`}
                                className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-900 border border-yellow-300 font-semibold text-xs"
                              >
                                ⚠️ {label}
                              </span>
                            );
                          })()}
                        </div>
                      );
                    })()}
                    <Badge
                      className={router.status === 'online' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                    >
                      {router.status || 'unknown'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Warning if IP not active in whitelist */}
                {router.whitelist_active === false && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3 dark:bg-orange-900/30">
                    <div className="flex gap-2">
                      <div className="text-orange-600 mt-0.5 dark:text-orange-400">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-orange-900">IP Tidak Aktif di Whitelist</p>
                        <p className="text-xs text-orange-800 mt-0.5">
                          IP {router.host} dinonaktifkan di whitelist. Aktifkan di tab IP Whitelist untuk menggunakan router ini.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2 text-sm">
                  {router.owner_name && (
                            <span data-testid="router-owner-badge" className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700 border border-purple-200 mr-1 dark:bg-purple-900/40 dark:text-purple-300">
                              👤 {router.owner_name}{router.owner_code ? ` (${router.owner_code})` : ''}
                            </span>
                          )}
                          {router.branch_name && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 w-16 dark:text-slate-400">Branch:</span>
                      <span className="font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded dark:text-indigo-400 dark:bg-indigo-900/30">{router.branch_name}</span>
                    </div>
                  )}
                  
                  {/* Connection Mode Badge */}
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 w-16 dark:text-slate-400">Mode:</span>
                    <Badge className={
                      router.connection_mode === 'hybrid' ? 'bg-purple-100 text-purple-800' :
                      router.connection_mode === 'radius_only' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }>
                      {router.connection_mode === 'hybrid' && '⭐ Hybrid'}
                      {router.connection_mode === 'radius_only' && '📊 RADIUS Only'}
                      {(!router.connection_mode || router.connection_mode === 'api_only') && '🔌 API Only'}
                    </Badge>
                  </div>

                  {/* Webhook Status Badge */}
                  {(() => {
                    const ws = webhookStatus[String(router.id)] || webhookStatus[router.id];
                    if (!ws) return null;
                    const colorMap = {
                      ok: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                      partial: 'bg-amber-100 text-amber-800 border-amber-300',
                      missing: 'bg-rose-100 text-rose-800 border-rose-300',
                      unreachable: 'bg-gray-100 text-gray-700 border-gray-300',
                      no_profile: 'bg-gray-100 text-gray-700 border-gray-300',
                    };
                    const labelMap = {
                      ok: 'Webhook OK (' + ws.ok + '/' + ws.total + ')',
                      partial: 'Webhook ' + ws.ok + '/' + ws.total,
                      missing: 'Webhook Missing',
                      unreachable: 'Unreachable',
                      no_profile: 'No PPP Profile',
                    };
                    return (
                      <div className="flex items-center gap-2" data-testid={'webhook-status-' + router.id}>
                        <span className="text-gray-500 w-16 dark:text-slate-400">Webhook:</span>
                        <Badge
                          variant="outline"
                          className={colorMap[ws.status] || colorMap.unreachable}
                          title={ws.error || ws.status}
                        >
                          {labelMap[ws.status] || ws.status}
                        </Badge>
                        {/* 2026-06-10: 1-click install/repair PPPoE webhook script
                            so admin tidak perlu copy-paste manual ke MikroTik.
                            Tombol hanya muncul saat status missing atau partial. */}
                        {(ws.status === 'missing' || ws.status === 'partial') && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={webhookSetupLoading === router.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetupWebhook(router);
                            }}
                            className="h-6 px-2 text-[10px] border-amber-300 text-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/30"
                            data-testid={'install-webhook-btn-' + router.id}
                            title="Pasang/perbaiki skrip on-up & on-down ke semua PPP profile router ini"
                          >
                            {webhookSetupLoading === router.id ? 'Memasang...' : 'Pasang'}
                          </Button>
                        )}
                      </div>
                    );
                  })()}
                  
                  <DualVpnStatusRow
                    router={router}
                    status={dualVpn.statusMap[String(router.id)]}
                    isAdmin={isSuperAdmin}
                    busy={dualVpn.busyId === router.id}
                    onProvision={dualVpn.provision}
                  />

                  {router.location && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 w-16 dark:text-slate-400">Lokasi:</span>
                      <span className="font-medium text-gray-700 dark:text-slate-200">{router.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 w-16 dark:text-slate-400">Host:</span>
                    <span className="font-medium">{router.host}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 w-16 dark:text-slate-400">Port:</span>
                    <span className="font-medium">{router.port}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 w-16 dark:text-slate-400">User:</span>
                    <span className="font-medium">{router.username}</span>
                  </div>
                </div>

                <div className="space-y-2 pt-3 border-t">
                  {/* Row 1: Main Actions */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setScriptRouter(router);
                        setShowScriptModal(true);
                      }}
                      className="flex-1 text-xs bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white border-0"
                    >
                      <Copy className="h-3 w-3 mr-1" /> Copy Script
                    </Button>
                  </div>
                  
                  {/* Row 2a: Main sync actions (3 buttons equal width) */}
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRedeployIsolir(router.id, router.name || router.host);
                      }}
                      disabled={redeployingRouter === router.id}
                      title="Re-deploy NAT + Filter + Masquerade rules untuk isolir"
                      data-testid={`redeploy-isolir-btn-${router.id}`}
                      className="w-full text-xs text-amber-700 hover:bg-amber-50 border-amber-200 truncate dark:text-amber-300 dark:hover:bg-amber-900/30"
                    >
                      {redeployingRouter === router.id ? (
                        <><Activity className="h-3 w-3 mr-1 animate-spin" /> ...</>
                      ) : (
                        <><Shield className="h-3 w-3 mr-1 flex-shrink-0" /> <span className="truncate">Re-deploy</span></>
                      )}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSyncRouterProfiles(router);
                      }}
                      disabled={syncRouterProfilesLoading === router.id}
                      title="Sync IP Pool + Profile PPPoE dari DB ke MikroTik"
                      data-testid={`sync-profiles-btn-${router.id}`}
                      className="w-full text-xs text-blue-700 hover:bg-blue-50 border-blue-200 truncate dark:text-blue-300 dark:hover:bg-blue-900/30"
                    >
                      {syncRouterProfilesLoading === router.id ? (
                        <><Activity className="h-3 w-3 mr-1 animate-spin" /> ...</>
                      ) : (
                        <><RefreshCw className="h-3 w-3 mr-1 flex-shrink-0" /> <span className="truncate">Profiles</span></>
                      )}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSyncRadiusNow(router);
                      }}
                      disabled={syncRadiusLoading === router.id}
                      title="Refresh status online dari /ppp/active flag R (RADIUS)"
                      data-testid={`sync-radius-btn-${router.id}`}
                      className="w-full text-xs text-emerald-700 hover:bg-emerald-50 border-emerald-200 truncate dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                    >
                      {syncRadiusLoading === router.id ? (
                        <><Activity className="h-3 w-3 mr-1 animate-spin" /> ...</>
                      ) : (
                        <><Wifi className="h-3 w-3 mr-1 flex-shrink-0" /> <span className="truncate">RADIUS</span></>
                      )}
                    </Button>
                  </div>

                  {/* Row 2c (2026-05-28): Move customers wizard */}
                  <div className="grid grid-cols-1 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        openMoveCustModal(router);
                      }}
                      title="Pindahkan pelanggan ke router lain"
                      data-testid={`move-customers-btn-${router.id}`}
                      className="w-full text-xs text-purple-700 hover:bg-purple-50 border-purple-200 dark:text-purple-300 dark:hover:bg-purple-900/30"
                    >
                      <Users className="h-3 w-3 mr-1 flex-shrink-0" /> Pindah Pelanggan ke Router Lain
                    </Button>
                  </div>

                  {/* Row 2d (2026-05-30): Copy RADIUS secrets to local MikroTik (backup if RADIUS down) */}
                  <div className="grid grid-cols-1 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); handleCopySecretsPreview(router); }}
                      disabled={copySecretsLoading === router.id}
                      title="Salin secret PPPoE dari RADIUS ke /ppp/secret MikroTik sebagai backup offline (jika RADIUS down)"
                      data-testid={`copy-secrets-btn-${router.id}`}
                      className="w-full text-xs text-orange-700 hover:bg-orange-50 border-orange-300 dark:text-orange-300 dark:hover:bg-orange-900/30"
                    >
                      {copySecretsLoading === router.id ? (
                        <><Activity className="h-3 w-3 mr-1 animate-spin" /> Preview...</>
                      ) : (
                        <><Copy className="h-3 w-3 mr-1 flex-shrink-0" /> Backup Secret ke MikroTik</>
                      )}
                    </Button>
                  </div>

                  {/* Row 2b: Utility buttons (Test / Edit / Delete) */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTestConnection(router.id);
                      }}
                      disabled={testingRouter === router.id}
                      className="flex-1 text-xs"
                    >
                      {testingRouter === router.id ? (
                        <><Activity className="h-3 w-3 mr-1 animate-spin" /> Testing...</>
                      ) : (
                        <><TestTube className="h-3 w-3 mr-1" /> Test Koneksi</>
                      )}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(router);
                      }}
                      title="Edit Router"
                      className="text-xs hover:bg-gray-50 px-3 dark:hover:bg-slate-700/50"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteClick(router);
                      }}
                      title="Hapus Router"
                      className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-3 dark:text-red-400 dark:hover:bg-red-900/30"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </>
      ) : (
        <>
      {/* IP Whitelist Content */}
      <div className="mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-900/30">
          <div className="flex gap-2">
            <div className="text-blue-600 mt-0.5 dark:text-blue-400">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-1">IP Whitelist</h3>
              <p className="text-xs text-blue-800">
                Daftar IP yang diizinkan untuk ditambahkan sebagai router MikroTik. 
                IP harus terdaftar di whitelist sebelum dapat menambahkan router.
              </p>
            </div>
          </div>
        </div>
        
        {/* Search Bar */}
        {whitelist.length > 0 && (
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Cari IP address atau deskripsi..."
                value={whitelistSearchQuery}
                onChange={(e) => {
                  setWhitelistSearchQuery(e.target.value);
                  setWhitelistCurrentPage(1); // Reset to first page on search
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {(() => {
        // Filter whitelist based on search query
        const filteredWhitelist = whitelist.filter(entry => {
          if (!whitelistSearchQuery) return true;
          const query = whitelistSearchQuery.toLowerCase();
          return (
            entry.ip_address.toLowerCase().includes(query) ||
            (entry.description && entry.description.toLowerCase().includes(query))
          );
        });

        // Calculate pagination
        const totalPages = Math.ceil(filteredWhitelist.length / whitelistItemsPerPage);
        const startIndex = (whitelistCurrentPage - 1) * whitelistItemsPerPage;
        const endIndex = startIndex + whitelistItemsPerPage;
        const paginatedWhitelist = filteredWhitelist.slice(startIndex, endIndex);

        if (whitelist.length === 0) {
          return (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="p-4 bg-gray-100 rounded-full dark:bg-slate-800">
                    <Check className="h-8 w-8 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 dark:text-slate-100">Belum Ada IP di Whitelist</h3>
                    <p className="text-sm text-gray-600 dark:text-slate-300">
                      Tambahkan IP MikroTik ke whitelist sebelum menambahkan router
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        }

        if (filteredWhitelist.length === 0) {
          return (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="p-4 bg-gray-100 rounded-full dark:bg-slate-800">
                    <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 dark:text-slate-100">Tidak Ada Hasil</h3>
                    <p className="text-sm text-gray-600 dark:text-slate-300">
                      Tidak ditemukan IP yang sesuai dengan pencarian &quot;{whitelistSearchQuery}&quot;
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        }

        return (
          <>
            <div className="space-y-3">
              {paginatedWhitelist.map((entry) => (
            <Card key={entry.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${entry.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Check className={`h-4 w-4 ${entry.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold font-mono ${entry.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                            {entry.ip_address}
                          </p>
                          <Badge className={`text-xs ${entry.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {entry.is_active ? 'Aktif' : 'Non-Aktif'}
                          </Badge>
                        </div>
                        <p className={`text-xs mt-0.5 ${entry.is_active ? 'text-gray-600' : 'text-gray-400'}`}>
                          {entry.description}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Ditambahkan: {new Date(entry.created_at).toLocaleDateString('id-ID')} oleh {entry.created_by_name || 'System'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Edit Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditWhitelist(entry)}
                      className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
                      title="Edit IP"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    
                    {/* Toggle Active/Inactive Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleWhitelistStatus(entry.id, entry.is_active)}
                      className={`text-xs ${
                        entry.is_active 
                          ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' 
                          : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                      }`}
                      title={entry.is_active ? 'Nonaktifkan IP' : 'Aktifkan IP'}
                    >
                      <Power className="h-3 w-3" />
                    </Button>
                    
                    {/* Delete Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setWhitelistToDelete(entry);
                        setShowDeleteWhitelistModal(true);
                      }}
                      className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                      title="Hapus IP"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-slate-700">
                <div className="text-sm text-gray-600 dark:text-slate-300">
                  Menampilkan {startIndex + 1}-{Math.min(endIndex, filteredWhitelist.length)} dari {filteredWhitelist.length} IP
                  {whitelistSearchQuery && ` (hasil pencarian)`}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setWhitelistCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={whitelistCurrentPage === 1}
                    className="text-xs"
                  >
                    ← Sebelumnya
                  </Button>
                  
                  <div className="flex gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= whitelistCurrentPage - 1 && page <= whitelistCurrentPage + 1)
                      ) {
                        return (
                          <Button
                            key={page}
                            variant={page === whitelistCurrentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => setWhitelistCurrentPage(page)}
                            className={`text-xs min-w-[32px] ${
                              page === whitelistCurrentPage 
                                ? 'bg-indigo-600 text-white' 
                                : ''
                            }`}
                          >
                            {page}
                          </Button>
                        );
                      } else if (
                        page === whitelistCurrentPage - 2 ||
                        page === whitelistCurrentPage + 2
                      ) {
                        return <span key={page} className="px-2 text-gray-400">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setWhitelistCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={whitelistCurrentPage === totalPages}
                    className="text-xs"
                  >
                    Berikutnya →
                  </Button>
                </div>
              </div>
            )}
          </>
        );
      })()}
      </>
      )}
      
      {/* Backup Secrets Tab Content */}
      {activeTab === 'backup' && (
        <div className="space-y-6">
          {/* Info Banner */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 dark:bg-orange-900/30">
            <div className="flex gap-3">
              <AlertTriangle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-0.5 dark:text-orange-400" />
              <div>
                <h3 className="font-semibold text-orange-900 mb-1">RADIUS Failover - Backup Secrets</h3>
                <p className="text-sm text-orange-800 mb-3">
                  Fitur ini akan menyalin semua kredensial PPPoE pelanggan ke MikroTik sebagai backup.
                  Secret akan dibuat dalam kondisi <strong>DISABLED</strong> dan hanya diaktifkan manual saat RADIUS server offline.
                </p>
                <ul className="text-xs text-orange-700 space-y-1 list-disc list-inside dark:text-orange-300">
                  <li>Secret backup dibuat dengan comment <code>[BACKUP]</code></li>
                  <li>Auto-sync: setiap buat/update pelanggan, secret backup otomatis di-update</li>
                  <li>Jika RADIUS offline, aktifkan semua backup secrets untuk failover</li>
                  <li>Setelah RADIUS online, nonaktifkan kembali backup secrets</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Cards - Step by Step */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Step 1: Sync Profiles */}
            <Card className="border-2 border-purple-200 hover:border-purple-400 transition-colors">
              <CardContent className="p-5">
                <div className="flex flex-col items-center text-center">
                  <div className="absolute -mt-8 bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    STEP 1
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full mb-3 mt-2 dark:bg-purple-900/40">
                    <Settings className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm dark:text-slate-100">Sync Profiles</h3>
                  <p className="text-xs text-gray-600 mb-3 dark:text-slate-300">
                    Sync semua paket layanan ke MikroTik sebagai PPPoE/Hotspot profile
                  </p>
                  <Button
                    onClick={handleSyncProfiles}
                    disabled={profileSyncing}
                    size="sm"
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {profileSyncing ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Settings className="h-3 w-3 mr-1" />
                        Sync Profiles
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Step 2: Sync Secrets */}
            <Card className="border-2 border-blue-200 hover:border-blue-400 transition-colors">
              <CardContent className="p-5">
                <div className="flex flex-col items-center text-center">
                  <div className="absolute -mt-8 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    STEP 2
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full mb-3 mt-2 dark:bg-blue-900/40">
                    <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm dark:text-slate-100">Sync Secrets</h3>
                  <p className="text-xs text-gray-600 mb-3 dark:text-slate-300">
                    Copy kredensial PPPoE pelanggan sebagai backup (disabled)
                  </p>
                  <Button
                    onClick={handleSyncBackupSecrets}
                    disabled={backupSyncing}
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {backupSyncing ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Users className="h-3 w-3 mr-1" />
                        Sync Secrets
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Step 3: Enable (RADIUS Offline) */}
            <Card className="border-2 border-green-200 hover:border-green-400 transition-colors">
              <CardContent className="p-5">
                <div className="flex flex-col items-center text-center">
                  <div className="absolute -mt-8 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    RADIUS DOWN
                  </div>
                  <div className="p-3 bg-green-100 rounded-full mb-3 mt-2 dark:bg-green-900/40">
                    <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm dark:text-slate-100">Enable Backup</h3>
                  <p className="text-xs text-gray-600 mb-3 dark:text-slate-300">
                    Aktifkan semua backup secrets saat RADIUS offline
                  </p>
                  <Button
                    onClick={handleEnableBackupSecrets}
                    disabled={backupEnabling}
                    size="sm"
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {backupEnabling ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Enabling...
                      </>
                    ) : (
                      <>
                        <Shield className="h-3 w-3 mr-1" />
                        Enable Semua
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Step 4: Disable (RADIUS Online) */}
            <Card className="border-2 border-gray-200 hover:border-gray-400 transition-colors dark:border-slate-700">
              <CardContent className="p-5">
                <div className="flex flex-col items-center text-center">
                  <div className="absolute -mt-8 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    RADIUS UP
                  </div>
                  <div className="p-3 bg-gray-100 rounded-full mb-3 mt-2 dark:bg-slate-800">
                    <ShieldOff className="h-6 w-6 text-gray-600 dark:text-slate-300" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm dark:text-slate-100">Disable Backup</h3>
                  <p className="text-xs text-gray-600 mb-3 dark:text-slate-300">
                    Nonaktifkan backup, kembali ke mode RADIUS
                  </p>
                  <Button
                    onClick={handleDisableBackupSecrets}
                    disabled={backupDisabling}
                    size="sm"
                    variant="outline"
                    className="w-full"
                  >
                    {backupDisabling ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Disabling...
                      </>
                    ) : (
                      <>
                        <ShieldOff className="h-3 w-3 mr-1" />
                        Disable Semua
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Profile Sync Result */}
          {profileSyncResult && (
            <Card className="border-2 border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  Hasil Sync Profiles
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg dark:bg-slate-900">
                    <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{profileSyncResult.total || 0}</p>
                    <p className="text-xs text-gray-600 dark:text-slate-300">Total Paket</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg dark:bg-green-900/30">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{profileSyncResult.synced || 0}</p>
                    <p className="text-xs text-gray-600 dark:text-slate-300">Berhasil</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg dark:bg-red-900/30">
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">{profileSyncResult.failed || 0}</p>
                    <p className="text-xs text-gray-600 dark:text-slate-300">Gagal</p>
                  </div>
                </div>
                
                {profileSyncResult.details && profileSyncResult.details.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0 dark:bg-slate-900">
                          <tr>
                            <th className="px-3 py-2 text-left">Paket</th>
                            <th className="px-3 py-2 text-left">Profile</th>
                            <th className="px-3 py-2 text-left">Rate Limit</th>
                            <th className="px-3 py-2 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {profileSyncResult.details.map((item, idx) => (
                            <tr key={idx} className={item.success ? '' : 'bg-red-50'}>
                              <td className="px-3 py-2">{item.plan}</td>
                              <td className="px-3 py-2 font-mono text-xs">{item.profile}</td>
                              <td className="px-3 py-2 font-mono text-xs">{item.rate_limit}</td>
                              <td className="px-3 py-2 text-center">
                                {item.success ? (
                                  <CheckCircle className="h-4 w-4 text-green-600 inline dark:text-green-400" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-600 inline dark:text-red-400" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Secrets Sync Result */}
          {backupSyncResult && (
            <Card className="border-2 border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                  Hasil Sync Terakhir
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg dark:bg-slate-900">
                    <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{backupSyncResult.total || 0}</p>
                    <p className="text-xs text-gray-600 dark:text-slate-300">Total Pelanggan</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg dark:bg-green-900/30">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{backupSyncResult.synced || 0}</p>
                    <p className="text-xs text-gray-600 dark:text-slate-300">Berhasil Sync</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg dark:bg-red-900/30">
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">{backupSyncResult.failed || 0}</p>
                    <p className="text-xs text-gray-600 dark:text-slate-300">Gagal</p>
                  </div>
                </div>
                
                {backupSyncResult.details && backupSyncResult.details.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 border-b dark:bg-slate-900">
                      <p className="text-xs font-medium text-gray-700 dark:text-slate-200">Detail (max 20 ditampilkan)</p>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0 dark:bg-slate-900">
                          <tr>
                            <th className="px-3 py-2 text-left">Pelanggan</th>
                            <th className="px-3 py-2 text-left">Username</th>
                            <th className="px-3 py-2 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {backupSyncResult.details.map((item, idx) => (
                            <tr key={idx} className={item.success ? '' : 'bg-red-50'}>
                              <td className="px-3 py-2">{item.customer}</td>
                              <td className="px-3 py-2 font-mono">{item.username}</td>
                              <td className="px-3 py-2 text-center">
                                {item.success ? (
                                  <CheckCircle className="h-4 w-4 text-green-600 inline dark:text-green-400" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-600 inline dark:text-red-400" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* How it Works */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">📖 Cara Kerja Failover</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                <div className="p-4 bg-blue-50 rounded-lg dark:bg-blue-900/30">
                  <div className="text-2xl mb-2">1️⃣</div>
                  <p className="text-sm font-medium">Sync Secrets</p>
                  <p className="text-xs text-gray-600 mt-1 dark:text-slate-300">Klik "Sync Semua" untuk copy kredensial ke MikroTik</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg dark:bg-slate-900">
                  <div className="text-2xl mb-2">2️⃣</div>
                  <p className="text-sm font-medium">Auto-Sync</p>
                  <p className="text-xs text-gray-600 mt-1 dark:text-slate-300">Setiap pelanggan baru/update otomatis di-sync</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg dark:bg-red-900/30">
                  <div className="text-2xl mb-2">3️⃣</div>
                  <p className="text-sm font-medium">RADIUS Down?</p>
                  <p className="text-xs text-gray-600 mt-1 dark:text-slate-300">Klik "Enable Semua" untuk aktifkan backup</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg dark:bg-green-900/30">
                  <div className="text-2xl mb-2">4️⃣</div>
                  <p className="text-sm font-medium">RADIUS Up!</p>
                  <p className="text-xs text-gray-600 mt-1 dark:text-slate-300">Klik "Disable Semua" untuk kembali ke RADIUS</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-3xl w-full my-8 max-h-[90vh] flex flex-col dark:bg-slate-800">
            <div className="p-5 border-b border-gray-200 flex-shrink-0 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">
                  {editingRouter ? 'Edit Router' : 'Tambah Router Baru'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingRouter(null);
                    setConnectionType(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Step 1: Choose Connection Type (only for new router) */}
            {!editingRouter && !connectionType && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2 dark:text-slate-200">Pilih Tipe Koneksi MikroTik</h3>
                <p className="text-sm text-gray-500 mb-6 dark:text-slate-400">Bagaimana server akan terhubung ke MikroTik Anda?</p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Option 1: IP Publik */}
                  <button
                    type="button"
                    onClick={() => {
                      setConnectionType('public_ip');
                      setFormData({ 
                        ...formData, 
                        has_public_ip: true, 
                        auto_create_vpn: false,
                        radius_server_type: 'public',
                        radius_server_ip: '103.179.252.149'
                      });
                    }}
                    className="p-6 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all text-left group dark:border-slate-700 dark:hover:bg-green-900/30"
                  >
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-200 dark:bg-green-900/40">
                      <Globe className="w-7 h-7 text-green-600 dark:text-green-400" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-800 mb-2 dark:text-slate-200">🌐 IP Publik</h4>
                    <p className="text-sm text-gray-500 mb-3 dark:text-slate-400">MikroTik punya IP Publik sendiri yang bisa diakses dari internet</p>
                    <ul className="text-xs text-gray-400 space-y-1">
                      <li>✓ Koneksi langsung & cepat</li>
                      <li>✓ Setup lebih mudah</li>
                      <li>✓ Cocok untuk ISP dengan IP Publik</li>
                    </ul>
                  </button>

                  {/* Option 2: VPN Tunnel */}
                  <button
                    type="button"
                    onClick={() => {
                      setConnectionType('vpn_tunnel');
                      setFormData({ 
                        ...formData, 
                        has_public_ip: false, 
                        auto_create_vpn: true,
                        use_vpn_wizard: true,
                        radius_server_type: 'vpn',
                        radius_server_ip: '10.10.10.1'
                      });
                    }}
                    className="p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group dark:border-slate-700 dark:hover:bg-blue-900/30"
                  >
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-200 dark:bg-blue-900/40">
                      <Shield className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h4 className="text-lg font-bold text-gray-800 mb-2 dark:text-slate-200">🔒 VPN Tunnel</h4>
                    <p className="text-sm text-gray-500 mb-3 dark:text-slate-400">MikroTik tidak punya IP Publik, konek via VPN L2TP</p>
                    <ul className="text-xs text-gray-400 space-y-1">
                      <li>✓ Tidak perlu IP Publik</li>
                      <li>✓ Koneksi aman & terenkripsi</li>
                      <li>✓ Cocok untuk MikroTik di belakang NAT</li>
                    </ul>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Form (show when connection type selected OR editing) */}
            {(connectionType || editingRouter) && (
            <form onSubmit={handleSubmit} className="p-5 overflow-y-auto flex-1">
              {/* Connection Type Badge */}
              {!editingRouter && (
                <div className="mb-4 flex items-center justify-between">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                    connectionType === 'public_ip' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {connectionType === 'public_ip' ? (
                      <><Globe className="w-4 h-4" /> Koneksi IP Publik</>
                    ) : (
                      <><Shield className="w-4 h-4" /> Koneksi VPN Tunnel</>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setConnectionType(null)}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-slate-400"
                  >
                    ← Ganti tipe koneksi
                  </button>
                </div>
              )}

              {/* VPN Info Banner (for VPN tunnel) */}
              {connectionType === 'vpn_tunnel' && !editingRouter && (
                <div className="mb-4 space-y-3">
                  {/* Wizard toggle */}
                  <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="vpn-wizard-toggle-label">
                      <input
                        type="checkbox"
                        checked={formData.use_vpn_wizard}
                        onChange={(e) => setFormData({ ...formData, use_vpn_wizard: e.target.checked })}
                        className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:text-indigo-400"
                        data-testid="vpn-wizard-toggle-checkbox"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-semibold text-indigo-900">🪄 VPN Wizard (Zero-Config, Direkomendasikan)</span>
                        <p className="text-xs text-indigo-700 mt-1 dark:text-indigo-300">
                          Sistem akan otomatis: (1) buat kredensial VPN, (2) push L2TP client config ke MikroTik via API,
                          (3) tunggu tunnel UP, (4) push /radius + sync PPPoE profiles otomatis.
                          <strong> Tanpa copy-paste script.</strong>
                        </p>
                      </div>
                    </label>
                  </div>

                  {formData.use_vpn_wizard ? (
                    <div className="space-y-3">
                      {/* VPN User Existing Selector */}
                      {Array.isArray(vpnCredentials) && vpnCredentials.length > 0 && (
                        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg dark:bg-emerald-900/30">
                          <label className="block text-sm font-semibold text-emerald-900 mb-2">
                            🎯 Pakai VPN User yang Sudah Dialokasikan (Recommended)
                          </label>
                          <select
                            value={formData.existing_vpn_user_id}
                            onChange={(e) => {
                              const selId = e.target.value;
                              const sel = vpnCredentials.find(v => String(v.id) === String(selId));
                              setFormData({
                                ...formData,
                                existing_vpn_user_id: selId,
                                // When existing user picked, host (used as form Host field) reflects the VPN IP
                                host: sel ? (sel.local_ip || '') : formData.host,
                              });
                            }}
                            className="w-full h-10 px-3 py-2 text-sm border border-emerald-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-800"
                            data-testid="wizard-existing-vpn-select"
                          >
                            <option value="">— Tidak (push L2TP otomatis via IP publik sementara) —</option>
                            {vpnCredentials.map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.vpn_username || v.username} → {v.local_ip} {v.router_name ? `(${v.router_name})` : ''}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-emerald-700 mt-2 dark:text-emerald-300">
                            ✓ Pilih jika MikroTik sudah dial L2TP tunnel manual ke server. Sistem akan skip push L2TP & langsung push RADIUS via tunnel.
                          </p>
                        </div>
                      )}

                      <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg dark:bg-indigo-900/30">
                        <h4 className="font-semibold text-indigo-800 mb-2">📋 Langkah VPN Wizard:</h4>
                        {formData.existing_vpn_user_id ? (
                          <ol className="text-sm text-indigo-700 space-y-1 list-decimal list-inside dark:text-indigo-300">
                            <li>Pastikan MikroTik sudah dial L2TP ke server (status di "Kelola VPN" = Connected)</li>
                            <li>Isi nama router, branch, lokasi</li>
                            <li>Isi username & password API MikroTik</li>
                            <li>Klik Simpan — sistem akan push /radius config + sync profiles via tunnel</li>
                          </ol>
                        ) : (
                          <ol className="text-sm text-indigo-700 space-y-1 list-decimal list-inside dark:text-indigo-300">
                            <li>Pastikan MikroTik saat ini punya IP publik <strong>sementara</strong> yang bisa diakses dari server billing</li>
                            <li>Isi <strong>Temporary Public IP MikroTik</strong> di field Host (untuk push L2TP awal)</li>
                            <li>Isi username & password API MikroTik (admin/...)</li>
                            <li>Klik Simpan — sistem akan menyelesaikan semuanya otomatis (~30-90 detik)</li>
                            <li>Setelah selesai, host router akan diganti ke <strong>10.10.10.x</strong> (VPN IP) otomatis</li>
                          </ol>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/30">
                      <h4 className="font-semibold text-blue-800 mb-2">📋 Langkah Koneksi VPN (Manual):</h4>
                      <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside dark:text-blue-300">
                        <li>Isi form dan klik Simpan</li>
                        <li>Sistem akan membuat kredensial VPN dan script MikroTik</li>
                        <li>Copy script dan jalankan di MikroTik Terminal</li>
                        <li>Setelah VPN konek, update IP router dengan IP VPN (10.10.10.x)</li>
                      </ol>
                    </div>
                  )}
                </div>
              )}

              {/* 2 Column Grid Layout */}
              <div className="grid grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                      Nama Router *
                    </label>
                    <Input
                      required
                      placeholder="MikroTik Main Router"
                      value={formData.router_name}
                      onChange={(e) => setFormData({ ...formData, router_name: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                      Lokasi
                    </label>
                    <Input
                      placeholder="Kantor Pusat / Cabang A / Tower 3"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                      Branch / Cabang *
                    </label>
                    <select
                      required
                      className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
                      value={formData.branch_id}
                      onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                    >
                      <option value="">-- Pilih Branch --</option>
                      {branches.filter(b => b.is_active).map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name} - {branch.address}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                      Pilih cabang untuk mengelompokkan router
                    </p>
                  </div>

                  {/* 2026-05-31: Owner assignment (hidden for role=owner) */}
                  {!isOwnerRoleForForm && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                        Owner / Pemilik Router
                      </label>
                      <select
                        data-testid="router-owner-select"
                        className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-600"
                        value={formData.owner_id || ''}
                        onChange={(e) => setFormData({ ...formData, owner_id: e.target.value })}
                      >
                        <option value="">-- Belum di-assign (visible: super_admin/admin/kepala_cabang) --</option>
                        {(() => {
                          // 2026-02: Split owners into branch-match (top) and other-branch (grey)
                          const sameBranch = owners.filter(o => !formData.branch_id || String(o.branch_id) === String(formData.branch_id));
                          const otherBranch = owners.filter(o => formData.branch_id && String(o.branch_id) !== String(formData.branch_id));
                          return (
                            <>
                              {sameBranch.map((o) => (
                                <option key={`s-${o.id}`} value={o.id}>
                                  {o.name}{o.code ? ` (@${o.code})` : ''}
                                </option>
                              ))}
                              {otherBranch.length > 0 && (
                                <optgroup label="── Owner dari Cabang Lain (shared/pool) ──">
                                  {otherBranch.map((o) => (
                                    <option key={`o-${o.id}`} value={o.id}>
                                      {o.name}{o.code ? ` (@${o.code})` : ''} — cabang: {o.branch_name || 'lain'}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                            </>
                          );
                        })()}
                      </select>
                      <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                        Owner yang ditugaskan akan otomatis melihat router ini di dashboard mereka. Kosongkan untuk router pool/shared.
                      </p>
                    </div>
                  )}

                  {/* Router Connection Type - VPN (only show when editing) */}
                  {editingRouter && formData.connection_mode !== 'radius_only' && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border dark:bg-slate-900">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!formData.has_public_ip}
                          onChange={(e) => {
                            setFormData({ ...formData, has_public_ip: !e.target.checked, auto_create_vpn: false });
                            setVpnTestStatus(null);
                            setApiTestStatus(null);
                          }}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600 dark:text-indigo-400"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-slate-200">🔒 Router tidak punya IP Publik (konek via VPN/Tunnel)</span>
                      </label>
                      
                      {!formData.has_public_ip && (
                        <div className="mt-3 space-y-3">
                          {/* Auto Create VPN Option */}
                          {!editingRouter && (
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/30">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.auto_create_vpn}
                                  onChange={(e) => setFormData({ ...formData, auto_create_vpn: e.target.checked })}
                                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 dark:border-slate-600 dark:text-green-400"
                                />
                                <span className="text-sm font-medium text-green-800">🚀 Buat VPN Otomatis</span>
                              </label>
                              <p className="text-xs text-green-700 mt-1 ml-6 dark:text-green-300">
                                Sistem akan otomatis membuat kredensial VPN dan script MikroTik untuk Anda
                              </p>
                            </div>
                          )}

                          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/30">
                            <p className="text-sm font-medium text-blue-800 mb-1">💡 Cara Koneksi VPN</p>
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                              {formData.auto_create_vpn 
                                ? "Setelah simpan, Anda akan mendapat script untuk dijalankan di MikroTik. Router akan konek ke VPN server dan bisa diakses dari aplikasi ini."
                                : "MikroTik Anda konek ke tunnel service (misal tunnel.id/ngrok) → mendapat domain/IP publik → masukkan domain publik tersebut ke field Host"
                              }
                            </p>
                          </div>
                          
                          {/* VPN User Selection - untuk internal VPN IP */}
                          {!formData.auto_create_vpn && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-slate-300">
                              Internal VPN IP (Opsional - hanya untuk routing internal)
                            </label>
                            <select
                              className="w-full h-9 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-slate-600"
                              value={selectedVpnUser}
                              onChange={(e) => handleVpnUserSelect(e.target.value)}
                            >
                              <option value="">-- Pilih VPN User atau kosongkan --</option>
                              {Array.isArray(vpnCredentials) && vpnCredentials.map((vpn, index) => {
                                const username = typeof vpn === 'string' ? vpn : vpn.username || vpn.name;
                                const ip = typeof vpn === 'object' ? (vpn.local_ip || vpn.ip || '') : '';
                                return (
                                  <option key={username || index} value={username}>
                                    {username}{ip ? ` - IP: ${ip}` : ''}
                                  </option>
                                );
                              })}
                            </select>
                            <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                              IP VPN internal antara server dan router (opsional)
                            </p>
                          </div>
                          )}
                          
                          {!formData.auto_create_vpn && (
                          <p className="text-xs text-orange-600 dark:text-orange-400">
                            ⚠️ Host field diisi dengan domain/IP publik dari tunnel service, bukan IP VPN internal
                          </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  
                  {/* Auto-detect VPN IP Section */}
                  {unconfiguredVpn.length > 0 && (
                    <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/30">
                      <p className="text-sm font-medium text-green-800 mb-2">
                        VPN Aktif Terdeteksi ({unconfiguredVpn.length} belum dikonfigurasi)
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {unconfiguredVpn.map((vpn) => (
                          <button
                            key={vpn.ip}
                            type="button"
                            onClick={() => {
                              // Also set existing_vpn_user_id by matching local_ip in vpnCredentials
                              const matched = Array.isArray(vpnCredentials) ? vpnCredentials.find(v => v.local_ip === vpn.ip) : null;
                              setFormData({ 
                                ...formData, 
                                host: vpn.ip,
                                router_name: vpn.suggested_name && !vpn.suggested_name.startsWith('Router-') && !vpn.suggested_name.startsWith('Unknown-') 
                                  ? vpn.suggested_name 
                                  : formData.router_name,
                                existing_vpn_user_id: matched ? String(matched.id) : ''
                              });
                              if (matched) {
                                toast.success('IP ' + vpn.ip + ' dipilih (VPN user ID: ' + matched.id + ')');
                              } else {
                                toast.warning('IP ' + vpn.ip + ' dipilih, tapi VPN user tidak match. Pilih dari dropdown manual.');
                              }
                            }}
                            className="px-3 py-1 text-sm bg-green-100 hover:bg-green-200 text-green-800 rounded-lg border border-green-300 transition-colors dark:bg-green-900/40"
                          >
                            {vpn.interface}: {vpn.ip}
                            {vpn.suggested_name && !vpn.suggested_name.startsWith('Router-') && !vpn.suggested_name.startsWith('Unknown-') && (
                              <span className="ml-1 text-green-600 dark:text-green-400">({vpn.suggested_name})</span>
                            )}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-green-600 mt-2 dark:text-green-400">
                        Klik IP untuk auto-fill ke form
                      </p>
                    </div>
                  )}

                  {activeVpnConnections.length > 0 && unconfiguredVpn.length === 0 && !editingRouter && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/30">
                      <p className="text-sm text-blue-800">
                        Semua VPN aktif sudah dikonfigurasi ({activeVpnConnections.length} router)
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                        {formData.use_vpn_wizard && !editingRouter
                          ? (formData.existing_vpn_user_id
                              ? <>VPN IP MikroTik * <span className="text-xs text-emerald-600 dark:text-emerald-400">(dari VPN user yang dipilih)</span></>
                              : <>Temporary Public IP MikroTik * <span className="text-xs text-indigo-600 dark:text-indigo-400">(untuk push L2TP awal)</span></>)
                          : <>Host/IP Address * {!formData.has_public_ip && <span className="text-xs text-blue-600 dark:text-blue-400">{formData.auto_create_vpn ? '(VPN IP setelah konek)' : '(Tunnel Public)'}</span>}</>
                        }
                      </label>
                      <Input
                        required={formData.use_vpn_wizard || !formData.auto_create_vpn}
                        disabled={(formData.auto_create_vpn && !formData.use_vpn_wizard) || (formData.use_vpn_wizard && !!formData.existing_vpn_user_id)}
                        placeholder={
                          formData.use_vpn_wizard
                            ? (formData.existing_vpn_user_id ? "Otomatis dari VPN user" : "203.0.113.50 (IP publik sementara MikroTik)")
                            : (formData.auto_create_vpn ? "Akan diisi otomatis setelah VPN konek" : (formData.has_public_ip ? "103.179.252.223" : "tenant1.tunnel.id"))
                        }
                        value={
                          formData.use_vpn_wizard
                            ? formData.host
                            : (formData.auto_create_vpn ? "10.10.10.x (akan diisi setelah VPN konek)" : formData.host)
                        }
                        onChange={(e) => {
                          setFormData({ ...formData, host: e.target.value });
                          setVpnTestStatus(null);
                          setApiTestStatus(null);
                        }}
                        data-testid="router-host-input"
                      />
                      <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                        {formData.use_vpn_wizard
                          ? "IP publik MikroTik saat ini (digunakan sekali untuk push L2TP client config). Setelah tunnel UP, host akan otomatis berubah ke 10.10.10.x."
                          : (formData.auto_create_vpn 
                              ? "IP akan didapat setelah MikroTik konek ke VPN server"
                              : (formData.has_public_ip 
                                  ? "IP publik atau hostname router" 
                                  : "Domain/IP publik dari tunnel service (misal: tenant1.tunnel.id)"))
                        }
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                        Port API
                      </label>
                      <div className="flex gap-2 items-stretch">
                        <Input
                          type="number"
                          placeholder="8728"
                          value={formData.port}
                          onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                          className="flex-1 min-w-0"
                          data-testid="router-port-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={detectApiPort}
                          disabled={detectingPort || !formData.host}
                          className="whitespace-nowrap shrink-0 px-2"
                          title="Auto-detect Port API"
                          data-testid="router-detect-port-btn"
                        >
                          {detectingPort ? '⏳' : '🔍'}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                        Default: <span className="font-mono">8728</span> (plain) atau <span className="font-mono">8729</span> (SSL). 
                        <span className="text-amber-600 dark:text-amber-400"> Jangan pakai 8291 (WinBox)!</span>
                      </p>
                    </div>
                  </div>

                  {/* Test Buttons for VPN mode */}
                  {formData.connection_mode !== 'radius_only' && !formData.has_public_ip && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/30">
                      <p className="text-xs font-medium text-blue-800 mb-2">🔍 Test Koneksi (Rekomendasi)</p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={testVpnConnection}
                          disabled={vpnTestStatus === 'testing' || !formData.host}
                          className={`flex-1 ${vpnTestStatus === 'success' ? 'border-green-500 bg-green-50' : vpnTestStatus === 'failed' ? 'border-red-500 bg-red-50' : ''}`}
                        >
                          {vpnTestStatus === 'testing' ? (
                            <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Testing...</>
                          ) : vpnTestStatus === 'success' ? (
                            <><CheckCircle className="w-4 h-4 mr-1 text-green-600 dark:text-green-400" /> VPN OK</>
                          ) : vpnTestStatus === 'failed' ? (
                            <><XCircle className="w-4 h-4 mr-1 text-red-600 dark:text-red-400" /> VPN Gagal</>
                          ) : (
                            '🔒 Test VPN'
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={testApiConnection}
                          disabled={apiTestStatus === 'testing' || !formData.host || !formData.username}
                          className={`flex-1 ${apiTestStatus === 'success' ? 'border-green-500 bg-green-50' : apiTestStatus === 'failed' ? 'border-red-500 bg-red-50' : ''}`}
                        >
                          {apiTestStatus === 'testing' ? (
                            <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Testing...</>
                          ) : apiTestStatus === 'success' ? (
                            <><CheckCircle className="w-4 h-4 mr-1 text-green-600 dark:text-green-400" /> API OK</>
                          ) : apiTestStatus === 'failed' ? (
                            <><XCircle className="w-4 h-4 mr-1 text-red-600 dark:text-red-400" /> API Gagal</>
                          ) : (
                            '🔌 Test API'
                          )}
                        </Button>
                      </div>
                      {(vpnTestMessage || apiTestMessage) && (
                        <div className="mt-2 text-xs">
                          {vpnTestMessage && <p className={vpnTestStatus === 'success' ? 'text-green-700' : 'text-red-700'}>VPN: {vpnTestMessage}</p>}
                          {apiTestMessage && <p className={apiTestStatus === 'success' ? 'text-green-700' : 'text-red-700'}>API: {apiTestMessage}</p>}
                        </div>
                      )}
                      {(vpnTestStatus === 'failed' || apiTestStatus === 'failed') && (
                        <p className="mt-2 text-xs text-orange-600 dark:text-orange-400">
                          💡 <strong>Catatan:</strong> Test mungkin gagal di preview karena server ini tidak punya VPN tunnel ke MikroTik. 
                          Jika yakin konfigurasi benar, Anda tetap bisa menyimpan dan test di production.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Test Button for Public IP mode */}
                  {formData.connection_mode !== 'radius_only' && formData.has_public_ip && (
                    <div className="mt-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={testApiConnection}
                        disabled={apiTestStatus === 'testing' || !formData.host || !formData.username}
                        className={`w-full ${apiTestStatus === 'success' ? 'border-green-500 bg-green-50' : apiTestStatus === 'failed' ? 'border-red-500 bg-red-50' : ''}`}
                      >
                        {apiTestStatus === 'testing' ? (
                          <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Mengetes koneksi...</>
                        ) : apiTestStatus === 'success' ? (
                          <><CheckCircle className="w-4 h-4 mr-1 text-green-600 dark:text-green-400" /> Koneksi Berhasil - {apiTestMessage}</>
                        ) : apiTestStatus === 'failed' ? (
                          <><XCircle className="w-4 h-4 mr-1 text-red-600 dark:text-red-400" /> Koneksi Gagal</>
                        ) : (
                          '🔌 Test Koneksi MikroTik'
                        )}
                      </Button>
                      {apiTestStatus === 'failed' && apiTestMessage && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{apiTestMessage}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {/* API Credentials Section - Optional for RADIUS Only */}
                  <div className={`space-y-4 ${formData.connection_mode === 'radius_only' ? 'opacity-60' : ''}`}>
                    {formData.connection_mode === 'radius_only' && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-xs text-yellow-800 dark:bg-yellow-900/30">
                        ⚠️ <strong>Opsional untuk RADIUS Only</strong>: Username & Password API tidak diperlukan karena tidak menggunakan API MikroTik.
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                        Username {formData.connection_mode !== 'radius_only' && '*'}
                        {formData.connection_mode === 'radius_only' && <span className="text-xs text-gray-400 ml-1">(opsional)</span>}
                      </label>
                      <Input
                        required={formData.connection_mode !== 'radius_only'}
                        placeholder="admin"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                        Password {formData.connection_mode !== 'radius_only' && !editingRouter && '*'}
                        {formData.connection_mode === 'radius_only' && <span className="text-xs text-gray-400 ml-1">(opsional)</span>}
                        {editingRouter && formData.connection_mode !== 'radius_only' && <span className="text-xs text-gray-500 ml-1 dark:text-slate-400">(kosongkan jika tidak diubah)</span>}
                      </label>
                      <Input
                        type="password"
                        required={formData.connection_mode !== 'radius_only' && !editingRouter}
                        placeholder={editingRouter ? 'Biarkan kosong jika tidak diubah' : '••••••••'}
                        autoComplete="new-password"
                        data-lpignore="true"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* RADIUS Configuration in Right Column */}
                  <div className="border-t pt-4">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                      <Server className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      RADIUS Configuration
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                        Connection Mode *
                      </label>
                      <select
                        className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                        value={formData.connection_mode}
                        onChange={(e) => setFormData({ ...formData, connection_mode: e.target.value, radius_enabled: e.target.value !== 'api_only' })}
                      >
                        <option value="api_only">API Only (Simple - Current)</option>
                        <option value="radius_only">RADIUS Only (Professional)</option>
                        <option value="hybrid">Hybrid (API + RADIUS) ⭐</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                        {formData.connection_mode === 'api_only' && '✓ Real-time control via Mikrotik API'}
                        {formData.connection_mode === 'radius_only' && '✓ Detailed accounting & session logs'}
                        {formData.connection_mode === 'hybrid' && '✓ Real-time control + Detailed accounting'}
                      </p>
                    </div>

                {/* Show RADIUS fields if not API only */}
                {formData.connection_mode !== 'api_only' && (
                  <div className="mt-4 space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200 dark:bg-blue-900/30">
                    {/* RADIUS Server Type Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                        Tipe Koneksi RADIUS *
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, radius_server_type: 'public' })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            formData.radius_server_type === 'public'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium text-sm">🌐 IP Publik</div>
                          <div className="text-xs text-gray-500 dark:text-slate-400">RADIUS server punya IP publik</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, radius_server_type: 'vpn' })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            formData.radius_server_type === 'vpn'
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium text-sm">🔒 IP VPN</div>
                          <div className="text-xs text-gray-500 dark:text-slate-400">Koneksi via VPN tunnel</div>
                        </button>
                      </div>
                    </div>

                    {/* RADIUS Server Settings based on type */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                          {formData.radius_server_type === 'vpn' ? '🔒 RADIUS VPN IP *' : '🌐 RADIUS Server IP *'}
                        </label>
                        <Input
                          placeholder={formData.radius_server_type === 'vpn' ? "10.10.10.1" : "103.179.252.149"}
                          value={formData.radius_server_type === 'vpn' ? formData.radius_vpn_ip : formData.radius_server_ip}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            [formData.radius_server_type === 'vpn' ? 'radius_vpn_ip' : 'radius_server_ip']: e.target.value 
                          })}
                          required={formData.connection_mode !== 'api_only'}
                        />
                        <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                          {formData.radius_server_type === 'vpn' 
                            ? "IP RADIUS server di jaringan VPN" 
                            : "IP publik RADIUS server"
                          }
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                          NAS Identifier
                        </label>
                        <Input
                          placeholder="mikrotik-cabang-a"
                          value={formData.radius_nas_identifier}
                          onChange={(e) => setFormData({ ...formData, radius_nas_identifier: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                        RADIUS Shared Secret {!editingRouter && '*'}
                      </label>
                      <Input
                        type="password"
                        placeholder={editingRouter ? "Kosongkan jika tidak ingin mengubah" : "testing123"}
                        autoComplete="new-password"
                        data-lpignore="true"
                        value={formData.radius_shared_secret}
                        onChange={(e) => setFormData({ ...formData, radius_shared_secret: e.target.value })}
                        required={formData.connection_mode !== 'api_only' && !editingRouter}
                      />
                      <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                        {editingRouter 
                          ? "⚠️ Secret sudah tersimpan. Kosongkan field ini jika tidak ingin mengubah."
                          : "Secret untuk komunikasi antara Mikrotik dan RADIUS server"
                        }
                      </p>
                    </div>

                    {/* Port Settings */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                          Auth Port
                        </label>
                        <Input
                          type="number"
                          placeholder="1812"
                          value={formData.radius_auth_port}
                          onChange={(e) => setFormData({ ...formData, radius_auth_port: parseInt(e.target.value) || 1812 })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                          Acct Port
                        </label>
                        <Input
                          type="number"
                          placeholder="1813"
                          value={formData.radius_acct_port}
                          onChange={(e) => setFormData({ ...formData, radius_acct_port: parseInt(e.target.value) || 1813 })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                          CoA Port
                        </label>
                        <Input
                          type="number"
                          placeholder="3799"
                          value={formData.radius_coa_port}
                          onChange={(e) => setFormData({ ...formData, radius_coa_port: parseInt(e.target.value) || 3799 })}
                        />
                      </div>
                    </div>

                    {/* RADIUS Database Configuration - Multi-RADIUS Support */}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3 dark:bg-orange-900/30">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                        <span className="font-medium text-orange-800">Konfigurasi RADIUS Database (Multi-RADIUS)</span>
                      </div>
                      <p className="text-xs text-orange-700 dark:text-orange-300">
                        Opsional: Konfigurasi koneksi ke database RADIUS untuk router ini. 
                        Kosongkan untuk menggunakan RADIUS default (103.179.252.149).
                      </p>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                            RADIUS DB Host
                          </label>
                          <Input
                            placeholder="103.179.252.149 (default)"
                            value={formData.radius_host || ''}
                            onChange={(e) => setFormData({ ...formData, radius_host: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                            RADIUS DB Port
                          </label>
                          <Input
                            type="number"
                            placeholder="3306"
                            value={formData.radius_port || ''}
                            onChange={(e) => setFormData({ ...formData, radius_port: parseInt(e.target.value) || 3306 })}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                            DB Username
                          </label>
                          <Input
                            placeholder="radius"
                            value={formData.radius_db_user || ''}
                            onChange={(e) => setFormData({ ...formData, radius_db_user: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                            DB Password
                          </label>
                          <Input
                            type="password"
                            placeholder={editingRouter ? "Kosongkan jika tidak ingin mengubah" : "••••••••"}
                            autoComplete="new-password"
                            data-lpignore="true"
                            value={formData.radius_db_password || ''}
                            onChange={(e) => setFormData({ ...formData, radius_db_password: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-slate-200">
                            DB Name
                          </label>
                          <Input
                            placeholder="radius"
                            value={formData.radius_db_name || ''}
                            onChange={(e) => setFormData({ ...formData, radius_db_name: e.target.value })}
                          />
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        💡 Untuk migrasi: Router lama bisa tetap pakai RADIUS lama, router baru pakai RADIUS baru.
                        Sync customer akan otomatis ke RADIUS yang dikonfigurasi di router branch masing-masing.
                      </p>
                    </div>

                    <div className="bg-blue-100 border border-blue-300 rounded p-3 text-xs text-blue-800 dark:bg-blue-900/40">
                      <strong>📋 Konfigurasi di MikroTik:</strong>
                      <div className="mt-2 bg-white rounded p-2 font-mono text-xs dark:bg-slate-800">
                        <p className="text-gray-600 dark:text-slate-300"># Buka Winbox → RADIUS → Add New</p>
                        <p>Address: <span className="text-blue-700 font-bold dark:text-blue-300">
                          {formData.radius_server_type === 'vpn'
                            ? '10.10.10.1'
                            : (formData.radius_server_ip || '103.179.252.149')
                          }
                        </span>
                        {formData.radius_server_type === 'vpn' && <span className="text-green-600 ml-2 dark:text-green-400">(via VPN)</span>}
                        </p>
                        <p>Secret: <span className="text-blue-700 font-bold dark:text-blue-300">{formData.radius_shared_secret || 'your-secret'}</span></p>
                        <p>Authentication Port: <span className="text-blue-700 dark:text-blue-300">{formData.radius_auth_port || 1812}</span></p>
                        <p>Accounting Port: <span className="text-blue-700 dark:text-blue-300">{formData.radius_acct_port || 1813}</span></p>
                        <p>CoA Port: <span className="text-blue-700 dark:text-blue-300">{formData.radius_coa_port || 3799}</span></p>
                        <p>Service: <span className="text-green-700 dark:text-green-300">ppp ✓</span></p>
                        {formData.radius_server_type === 'vpn' && (
                          <p className="mt-2 text-orange-600 dark:text-orange-400">⚠️ Pastikan VPN tunnel ke RADIUS server aktif!</p>
                        )}
                        <p className="mt-2 text-gray-600 dark:text-slate-300"># PPP → Secrets → PPP Authentication</p>
                        <p>Use RADIUS: <span className="text-green-700 dark:text-green-300">yes ✓</span></p>
                        <p>Accounting: <span className="text-green-700 dark:text-green-300">yes ✓</span></p>
                        <p>Interim Update: <span className="text-blue-700 dark:text-blue-300">1m (opsional)</span></p>
                      </div>
                    </div>
                  </div>
                )}
                  </div>
                </div>
              </div>

              {/* Note - Full Width */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4 dark:bg-yellow-900/30">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> Pastikan MikroTik dapat diakses dari server ini. Port API harus terbuka dan di-whitelist.
                </p>
              </div>
            </form>
            )}

            {/* Footer with Buttons - only show when form is visible */}
            {(connectionType || editingRouter) && (
            <div className="p-5 border-t border-gray-200 flex justify-end gap-3 bg-white flex-shrink-0 dark:border-slate-700 dark:bg-slate-800">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingRouter(null);
                  resetForm();
                }}
              >
                Batal
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className={`${testsPassedOrSkipped() ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-500 hover:bg-orange-600'} disabled:opacity-50 disabled:cursor-not-allowed`}
                title={!testsPassedOrSkipped() ? 'Test belum berhasil - simpan dengan risiko sendiri' : 'Siap disimpan'}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Testing & Menyimpan...
                  </>
                ) : testsPassedOrSkipped() ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {editingRouter ? 'Update' : 'Simpan'}
                  </>
                ) : (
                  <>
                    <Activity className="h-4 w-4 mr-2" />
                    {editingRouter ? 'Update (Dengan Test Koneksi)' : 'Simpan (Dengan Test Koneksi)'}
                  </>
                )}
              </Button>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && routerToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full dark:bg-slate-800">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full dark:bg-red-900/40">
                  <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">Hapus Router?</h2>
                  <p className="text-sm text-gray-500 dark:text-slate-400">Tindakan ini tidak dapat dibatalkan</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 dark:bg-slate-900">
                <p className="text-sm text-gray-700 dark:text-slate-200">
                  Anda akan menghapus router:
                </p>
                <p className="text-base font-semibold text-gray-900 mt-2 dark:text-slate-100">
                  {routerToDelete.router_name}
                </p>
                <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                  Host: {routerToDelete.host}:{routerToDelete.port}
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelDelete}
                  className="flex-1"
                >
                  Batal
                </Button>
                <Button
                  type="button"
                  onClick={confirmDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Ya, Hapus
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Whitelist Modal */}
      {showAddWhitelistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full dark:bg-slate-800">
            <div className="p-5 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">
                  {editingWhitelist ? 'Edit IP Whitelist' : 'Tambah IP ke Whitelist'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddWhitelistModal(false);
                    setEditingWhitelist(null);
                    setWhitelistFormData({ ip_address: '', description: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleAddWhitelist}>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    IP Address *
                  </label>
                  <Input
                    required
                    type="text"
                    placeholder="103.179.252.66"
                    value={whitelistFormData.ip_address}
                    onChange={(e) => setWhitelistFormData({ ...whitelistFormData, ip_address: e.target.value })}
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                    Masukkan IP address router MikroTik (format: xxx.xxx.xxx.xxx)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-slate-200">
                    Deskripsi
                  </label>
                  <Input
                    type="text"
                    placeholder="Contoh: Router CCR1072 Cabang Depok"
                    value={whitelistFormData.description}
                    onChange={(e) => setWhitelistFormData({ ...whitelistFormData, description: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                    Opsional: Deskripsi untuk memudahkan identifikasi
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30">
                  <p className="text-xs text-blue-800">
                    <strong>Info:</strong> {editingWhitelist 
                      ? 'Perubahan akan langsung diterapkan setelah disimpan.' 
                      : 'Setelah IP ditambahkan ke whitelist, Anda dapat menambahkan router dengan IP tersebut di tab Router MikroTik.'}
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddWhitelistModal(false);
                      setEditingWhitelist(null);
                      setWhitelistFormData({ ip_address: '', description: '' });
                    }}
                    className="flex-1"
                  >
                    Batal
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <Activity className="h-4 w-4 mr-2 animate-spin" />
                        Menyimpan...
                      </>
                    ) : (
                      editingWhitelist ? 'Update IP' : 'Tambah ke Whitelist'
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Whitelist Confirmation Modal */}
      {showDeleteWhitelistModal && whitelistToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full dark:bg-slate-800">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full dark:bg-red-900/40">
                  <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">Hapus dari Whitelist?</h2>
                  <p className="text-sm text-gray-500 dark:text-slate-400">IP akan dihapus dari daftar whitelist</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 dark:bg-slate-900">
                <p className="text-sm text-gray-700 mb-2 dark:text-slate-200">
                  Anda akan menghapus IP:
                </p>
                <p className="text-base font-semibold text-gray-900 font-mono dark:text-slate-100">
                  {whitelistToDelete.ip_address}
                </p>
                <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                  {whitelistToDelete.description}
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 dark:bg-yellow-900/30">
                <p className="text-xs text-yellow-800">
                  <strong>Perhatian:</strong> IP hanya bisa dihapus jika tidak ada router yang menggunakannya.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowDeleteWhitelistModal(false);
                    setWhitelistToDelete(null);
                  }}
                  className="flex-1"
                >
                  Batal
                </Button>
                <Button
                  type="button"
                  onClick={handleDeleteWhitelist}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Ya, Hapus
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto Setup Isolir Modal */}
      {showIsolirSetupModal && isolirSetupRouter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl dark:bg-slate-800">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Auto Setup Isolir</h2>
                  <p className="text-sm text-gray-600 mt-1 dark:text-slate-300">
                    Router: <span className="font-semibold">{isolirSetupRouter.router_name}</span>
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowIsolirSetupModal(false);
                    setIsolirSetupRouter(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isolirSetupLoading}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 dark:bg-blue-900/30">
                <div className="flex gap-3">
                  <div className="text-blue-600 mt-0.5 dark:text-blue-400">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 mb-1">Apa yang akan dilakukan?</p>
                    <p className="text-xs text-blue-800">
                      Wizard ini akan otomatis mengkonfigurasi fitur <strong>Isolir</strong> (redirect pelanggan yang belum bayar) 
                      pada router MikroTik Anda. Server akan terhubung ke router dan menginjeksi konfigurasi secara otomatis.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 dark:bg-slate-800 dark:border-slate-700">
                <h3 className="font-semibold text-sm text-gray-900 mb-3 dark:text-slate-100">📋 Konfigurasi yang akan diterapkan:</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <div className="text-green-600 mt-0.5 dark:text-green-400">✓</div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-slate-100">IP Pool "pool-isolir"</p>
                      <p className="text-xs text-gray-600 dark:text-slate-300">Range: 10.99.99.2-10.99.99.254 untuk pelanggan isolir</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-green-600 mt-0.5 dark:text-green-400">✓</div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-slate-100">PPP Profile "ISOLIR"</p>
                      <p className="text-xs text-gray-600 dark:text-slate-300">Profile khusus dengan bandwidth 256k/256k</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-green-600 mt-0.5 dark:text-green-400">✓</div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-slate-100">Layer 7 Protocol</p>
                      <p className="text-xs text-gray-600 dark:text-slate-300">Untuk mendeteksi HTTP request dari pelanggan</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-green-600 mt-0.5 dark:text-green-400">✓</div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-slate-100">NAT Redirect Rule</p>
                      <p className="text-xs text-gray-600 dark:text-slate-300">Redirect HTTP ke halaman pemberitahuan</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-green-600 mt-0.5 dark:text-green-400">✓</div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-slate-100">Filter Rules</p>
                      <p className="text-xs text-gray-600 dark:text-slate-300">Block HTTPS & kontrol akses pelanggan isolir</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded text-xs text-gray-600 dark:bg-slate-900 dark:text-slate-300">
                  <p className="font-medium text-gray-800 mb-1 dark:text-slate-200">Cara Kerja:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Saat pelanggan di-isolir, profile PPPoE diubah ke "ISOLIR"</li>
                    <li>Pelanggan mendapat IP dari pool-isolir (10.99.99.x)</li>
                    <li>Traffic HTTP di-redirect ke halaman pemberitahuan</li>
                    <li>Saat unisolir, profile dikembalikan ke semula</li>
                  </ol>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 dark:bg-yellow-900/30">
                <div className="flex gap-2">
                  <div className="text-yellow-600 mt-0.5 dark:text-yellow-400">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-yellow-900 mb-1">⚠️ Perhatian:</p>
                    <ul className="list-disc list-inside text-xs text-yellow-800 space-y-1">
                      <li>Pastikan router dapat diakses dari server ini</li>
                      <li>Username dan password router harus sudah benar</li>
                      <li>Proses setup membutuhkan waktu ~20-30 detik</li>
                      <li>Jika sudah ada konfigurasi, akan diupdate otomatis</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-gray-600 dark:text-slate-300">
                    <p><strong>Router:</strong> {isolirSetupRouter.router_name}</p>
                    <p><strong>Host:</strong> {isolirSetupRouter.host}:{isolirSetupRouter.port}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowIsolirSetupModal(false);
                      setIsolirSetupRouter(null);
                    }}
                    disabled={isolirSetupLoading}
                    className="flex-1"
                  >
                    Batal
                  </Button>
                  <Button
                    onClick={handleAutoSetupIsolir}
                    disabled={isolirSetupLoading}
                    className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isolirSetupLoading ? (
                      <>
                        <Activity className="h-4 w-4 mr-2 animate-spin" />
                        Sedang Setup...
                      </>
                    ) : (
                      <>
                        <Settings className="h-4 w-4 mr-2" />
                        Mulai Auto Setup
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VPN Created Success Modal */}
      {vpnCreatedData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden dark:bg-slate-800">
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center dark:bg-green-900/40">
                  <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-slate-200">🎉 VPN Berhasil Dibuat!</h3>
                  <p className="text-sm text-gray-500 dark:text-slate-400">Copy script di bawah dan jalankan di MikroTik Terminal</p>
                </div>
              </div>

              {/* VPN Credentials */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4 dark:bg-slate-900">
                <h4 className="font-semibold text-gray-700 mb-3 dark:text-slate-200">📋 Kredensial VPN</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-slate-400">Server:</span>
                    <p className="font-mono font-medium">{vpnCreatedData.vpn_server}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-slate-400">Username:</span>
                    <p className="font-mono font-medium">{vpnCreatedData.vpn_username}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-slate-400">Password:</span>
                    <p className="font-mono font-medium">{vpnCreatedData.vpn_password}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-slate-400">SSH Synced:</span>
                    <p className={`font-medium ${vpnCreatedData.ssh_synced ? 'text-green-600' : 'text-orange-600'}`}>
                      {vpnCreatedData.ssh_synced ? '✅ Yes' : '⚠️ No - ' + (vpnCreatedData.ssh_message || 'Manual sync required')}
                    </p>
                  </div>
                </div>
              </div>

              {/* MikroTik Script */}
              <div className="bg-slate-900 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-white">🔧 Script MikroTik</h4>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(vpnCreatedData.mikrotik_setup || '');
                      toast.success('Script berhasil dicopy!');
                    }}
                    className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" /> Copy Script
                  </button>
                </div>
                <pre className="text-xs text-green-400 overflow-x-auto whitespace-pre-wrap font-mono">
                  {vpnCreatedData.mikrotik_setup || 'No script available'}
                </pre>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200 dark:bg-blue-900/30">
                <h4 className="font-semibold text-blue-800 mb-2">📝 Langkah Selanjutnya:</h4>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside dark:text-blue-300">
                  <li>Copy script di atas</li>
                  <li>Buka MikroTik Terminal (via Winbox/WebFig)</li>
                  <li>Paste dan jalankan script</li>
                  <li>Tunggu VPN terkoneksi (status: connected)</li>
                  <li>Cek IP yang didapat di interface L2TP</li>
                  <li>Update Host/IP di router settings dengan IP VPN</li>
                </ol>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setVpnCreatedData(null);
                    setShowAddModal(false);
                    setEditingRouter(null);
                    resetForm();
                    fetchRouters();
                    fetchVpnCredentials();
    fetchActiveVpn();
                  }}
                >
                  Tutup
                </Button>
                <Button
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => {
                    navigator.clipboard.writeText(vpnCreatedData.mikrotik_setup || '');
                    toast.success('Script berhasil dicopy!');
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy & Tutup
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MikroTik Setup Script Modal */}
      <DualVpnScriptModal data={dualVpn.script} onClose={() => dualVpn.setScript(null)} />

      {showScriptModal && scriptRouter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden dark:bg-slate-800">
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center dark:bg-green-900/40">
                    <Server className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-slate-200">Script Setup MikroTik</h3>
                    <p className="text-sm text-gray-500 dark:text-slate-400">{scriptRouter.router_name}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowScriptModal(false); setScriptRouter(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 dark:bg-blue-900/30">
                <p className="text-sm text-blue-800">
                  <strong>Cara Pakai:</strong> Copy script → Buka MikroTik Terminal → Paste → Enter
                </p>
              </div>

              {/* Single Script */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-teal-600 px-4 py-3 flex items-center justify-between">
                  <span className="text-white font-semibold">🚀 Script Lengkap (RADIUS + Isolir + Webhook)</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(generateMikroTikScripts(scriptRouter).full);
                      toast.success('Script berhasil dicopy!');
                    }}
                    className="px-3 py-1.5 bg-white text-green-700 text-sm rounded-lg flex items-center gap-1 hover:bg-green-50 dark:bg-slate-800 dark:text-green-300 dark:hover:bg-green-900/30"
                  >
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                </div>
                <pre className="bg-slate-900 p-4 text-xs text-green-400 overflow-x-auto max-h-80 font-mono whitespace-pre-wrap">
                  {generateMikroTikScripts(scriptRouter).full}
                </pre>
              </div>

              {/* Footer */}
              <div className="mt-4 flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowScriptModal(false); setScriptRouter(null); }}
                >
                  Tutup
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    navigator.clipboard.writeText(generateMikroTikScripts(scriptRouter).full);
                    toast.success('Script berhasil dicopy!');
                    setShowScriptModal(false);
                    setScriptRouter(null);
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy & Tutup
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2026-05-28: Move Customers Between Routers Wizard Modal */}
      {showMoveCustModal && moveSourceRouter && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col dark:bg-slate-800">
            {/* Header */}
            <div className="p-5 border-b border-gray-200 flex items-center justify-between dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg dark:bg-purple-900/40">
                  <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">Pindah Pelanggan Antar Router</h2>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    Sumber: <span className="font-semibold text-gray-700 dark:text-slate-200">{moveSourceRouter.name}</span>
                    <span className="mx-2">·</span>
                    <span className="text-blue-600 dark:text-blue-400">{moveCustomers.length} pelanggan total</span>
                  </p>
                </div>
              </div>
              <button
                onClick={closeMoveCustModal}
                disabled={moveExecuting}
                className="text-gray-400 hover:text-gray-700 disabled:opacity-50"
                data-testid="move-cust-close-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Target router selector */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block dark:text-slate-200">
                  Router Tujuan <span className="text-red-500">*</span>
                </label>
                <select
                  value={moveTargetRouterId}
                  onChange={(e) => setMoveTargetRouterId(e.target.value)}
                  disabled={moveExecuting}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-slate-600"
                  data-testid="move-target-router-select"
                >
                  <option value="">— Pilih router tujuan —</option>
                  {routers
                    .filter((r) => r.id !== moveSourceRouter.id && r.is_active)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.host}) {r.branch_name ? `· ${r.branch_name}` : ''}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                  Pastikan IP Pool & Profile PPPoE sudah disinkronisasi ke router tujuan sebelum memindahkan.
                </p>
              </div>

              {/* Search + select-all */}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Cari nama / username / id..."
                  value={moveSearch}
                  onChange={(e) => setMoveSearch(e.target.value)}
                  className="flex-1"
                  data-testid="move-cust-search"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectAllVisible}
                  data-testid="move-select-all-btn"
                >
                  Pilih Semua
                </Button>
                <span className="text-xs text-gray-600 whitespace-nowrap dark:text-slate-300">
                  {moveSelectedIds.size} dipilih
                </span>
              </div>

              {/* Customer list */}
              {moveLoadingList ? (
                <div className="py-10 text-center text-sm text-gray-500 dark:text-slate-400">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-purple-500" />
                  Memuat daftar pelanggan…
                </div>
              ) : filteredMoveCustomers.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500 dark:text-slate-400">
                  Tidak ada pelanggan yang cocok.
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden dark:border-slate-700">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0 dark:bg-slate-900">
                        <tr className="text-left text-xs text-gray-600 dark:text-slate-300">
                          <th className="px-3 py-2 w-10"></th>
                          <th className="px-3 py-2">Nama</th>
                          <th className="px-3 py-2">PPPoE Username</th>
                          <th className="px-3 py-2">Paket</th>
                          <th className="px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMoveCustomers.map((c) => (
                          <tr
                            key={c.id}
                            className={`border-t hover:bg-purple-50 cursor-pointer ${moveSelectedIds.has(c.id) ? 'bg-purple-50' : ''}`}
                            onClick={() => toggleMoveSelect(c.id)}
                            data-testid={`move-cust-row-${c.id}`}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={moveSelectedIds.has(c.id)}
                                onChange={() => toggleMoveSelect(c.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="accent-purple-600"
                              />
                            </td>
                            <td className="px-3 py-2 font-medium text-gray-800 dark:text-slate-200">{c.name}</td>
                            <td className="px-3 py-2 text-gray-600 font-mono text-xs dark:text-slate-300">{c.pppoe_username || '-'}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-slate-300">{c.service_plan_name || '-'}</td>
                            <td className="px-3 py-2">
                              {c.is_isolated ? (
                                <Badge className="bg-amber-100 text-amber-700 border-0 dark:bg-amber-900/40 dark:text-amber-300">Isolir</Badge>
                              ) : c.is_active ? (
                                <Badge className="bg-green-100 text-green-700 border-0 dark:bg-green-900/40 dark:text-green-300">Aktif</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-700 border-0 dark:bg-slate-800 dark:text-slate-200">Non-aktif</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Result panel */}
              {moveResult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm dark:bg-emerald-900/30">
                  <div className="flex items-center gap-2 font-semibold text-emerald-800">
                    <CheckCircle className="h-4 w-4" /> Migrasi Berhasil
                  </div>
                  <ul className="mt-2 text-emerald-700 space-y-0.5 ml-6 list-disc dark:text-emerald-300">
                    <li>Dipindahkan: <b>{moveResult.moved_count}</b> pelanggan</li>
                    <li>Sesi cache di-clear: <b>{moveResult.cleared_sessions}</b></li>
                    <li>Sumber → Tujuan: {moveResult.source?.name} → {moveResult.target?.name}</li>
                  </ul>
                  <p className="text-xs text-emerald-600 mt-2 dark:text-emerald-400">
                    Tip: customer mungkin perlu re-dial PPPoE (cabut/pasang colokan / restart ONT) agar segera muncul di router tujuan.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3 dark:border-slate-700 dark:bg-slate-900">
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Customer dipindahkan satu kali. Selalu pastikan profile & IP pool tujuan sudah tersinkronisasi.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeMoveCustModal}
                  disabled={moveExecuting}
                  data-testid="move-cust-cancel-btn"
                >
                  Tutup
                </Button>
                <Button
                  type="button"
                  onClick={executeMoveCustomers}
                  disabled={moveExecuting || moveSelectedIds.size === 0 || !moveTargetRouterId}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="move-cust-execute-btn"
                >
                  {moveExecuting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Memindahkan…</>
                  ) : (
                    <><Users className="h-4 w-4 mr-2" /> Pindahkan {moveSelectedIds.size > 0 ? `(${moveSelectedIds.size})` : ''}</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy RADIUS Secrets -> MikroTik Dialog (added 2026-05-30) */}
      <Dialog open={copySecretsModal.open} onOpenChange={(open) => { if (!open) setCopySecretsModal({ open: false, router: null, preview: null, executing: false, result: null }); }}>
        <DialogContent className="max-w-2xl" data-testid="copy-secrets-dialog">
          <DialogHeader>
            <DialogTitle>Backup Secret RADIUS ke MikroTik</DialogTitle>
            <DialogDescription>
              Router: <strong>{copySecretsModal.router?.name || copySecretsModal.router?.router_name}</strong> ({copySecretsModal.router?.host})
            </DialogDescription>
          </DialogHeader>

          {/* Loading preview */}
          {!copySecretsModal.preview && !copySecretsModal.result && (
            <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              <Activity className="w-5 h-5 mx-auto mb-2 animate-spin" /> Memuat preview...
            </div>
          )}

          {/* Preview state */}
          {copySecretsModal.preview && !copySecretsModal.result && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md bg-blue-50 p-3 text-center dark:bg-blue-900/30">
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{copySecretsModal.preview.total_eligible}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">Pelanggan Eligible</div>
                </div>
                <div className="rounded-md bg-emerald-50 p-3 text-center dark:bg-emerald-900/30">
                  <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{copySecretsModal.preview.to_add_or_added}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">Akan Ditambah</div>
                </div>
                <div className="rounded-md bg-amber-50 p-3 text-center dark:bg-amber-900/30">
                  <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">{copySecretsModal.preview.already_exists}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-300">Sudah Ada (Skip)</div>
                </div>
              </div>
              {copySecretsModal.preview.errors_count > 0 && (
                <div className="rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  <strong>{copySecretsModal.preview.errors_count}</strong> error saat membaca router. Cek detail di backend log.
                </div>
              )}
              {Array.isArray(copySecretsModal.preview.sample_added) && copySecretsModal.preview.sample_added.length > 0 && (
                <details className="rounded-md border border-slate-200 p-2 dark:border-slate-700">
                  <summary className="cursor-pointer text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Contoh yang akan ditambah ({copySecretsModal.preview.sample_added.length})
                  </summary>
                  <div className="mt-2 max-h-48 overflow-y-auto text-xs">
                    <table className="w-full">
                      <thead className="bg-slate-50 sticky top-0 dark:bg-slate-900">
                        <tr><th className="text-left p-1">Username</th><th className="text-left p-1">Profile</th><th className="text-left p-1">Nama</th></tr>
                      </thead>
                      <tbody>
                        {copySecretsModal.preview.sample_added.map((x, i) => (
                          <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                            <td className="p-1 font-mono">{x.username}</td>
                            <td className="p-1">{x.profile}</td>
                            <td className="p-1 text-slate-600 dark:text-slate-300">{x.customer_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
              <div className="rounded-md bg-slate-50 p-2 text-xs text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                <strong>Mode:</strong> ADD ONLY — secret yang sudah ada di MikroTik <strong>tidak akan diubah</strong>. Aman dijalankan berulang.
                <br /><strong>Status secret baru:</strong> <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40">DISABLED</span> (standby — tidak bentrok RADIUS). Aktifkan manual saat RADIUS down.
                <br /><strong>Comment marker:</strong> <code>[COPY-FROM-RADIUS YYYY-MM-DD]</code>
              </div>
            </div>
          )}

          {/* Result state */}
          {copySecretsModal.result && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-emerald-50 p-3 text-center dark:bg-emerald-900/30">
                <div className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">✓ {copySecretsModal.result.to_add_or_added}</div>
                <div className="text-xs text-slate-600 dark:text-slate-300">Secret berhasil ditambahkan ke MikroTik</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-md bg-amber-50 p-2 text-center dark:bg-amber-900/30">
                  <div className="font-semibold">{copySecretsModal.result.already_exists}</div>
                  <div className="text-slate-600 dark:text-slate-300">Sudah ada (skip)</div>
                </div>
                <div className="rounded-md bg-red-50 p-2 text-center dark:bg-red-900/30">
                  <div className="font-semibold">{copySecretsModal.result.errors_count}</div>
                  <div className="text-slate-600 dark:text-slate-300">Error</div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setCopySecretsModal({ open: false, router: null, preview: null, executing: false, result: null })} data-testid="copy-secrets-close">
              {copySecretsModal.result ? 'Tutup' : 'Batal'}
            </Button>
            {copySecretsModal.preview && !copySecretsModal.result && (
              <>
                <Button
                  variant="outline"
                  onClick={handleCopySecretsCleanup}
                  disabled={copySecretsModal.executing}
                  className="text-red-700 border-red-300 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30"
                  data-testid="copy-secrets-cleanup"
                  title="Hapus semua secret bertanda [COPY-FROM-RADIUS] di router ini"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Bersihkan [COPY-FROM-RADIUS]
                </Button>
                <Button
                  onClick={handleCopySecretsExecute}
                  disabled={copySecretsModal.executing || copySecretsModal.preview.to_add_or_added === 0}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="copy-secrets-execute"
                >
                  {copySecretsModal.executing ? (
                    <><Activity className="w-4 h-4 mr-2 animate-spin" /> Sedang menambah...</>
                  ) : (
                    <>Eksekusi Backup ({copySecretsModal.preview.to_add_or_added} secret)</>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};


export default MikrotikSettings;
